import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { counterpartyApi, API_BASE_URL } from '../services/api';
import { fetchSettlementModes, fetchBankDetailsByCode } from '../services/paymentApi';
import {
  parseLocalYMD,
  formatLocalYMD,
  generateFullCouponSchedule,
  getLastAndNextCouponDates,
  getDaysDifference,
  formatDate,
  excelPRICE,
  findCouponPeriodFromMaturity
} from '../utils/gsecUtils';
import { checkHolidayForCurrency, formatHolidayMessage } from '../utils/holidayValidation';
import { DATE_INPUT_MIN, DATE_INPUT_MAX, isDateInRange } from '../utils/dateBounds';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import './TransactionPage.css';
import GsecSellDealModal from '../components/GsecSellDealModal';
import GsecLetterPrintButton from '../components/GsecLetterPrintButton';
import GsecFinalApprovedLettersPanel from '../components/GsecFinalApprovedLettersPanel';
import DealConfirmationButton from '../components/DealConfirmationButton';
import { GSEC_LETTER_ROLES } from '../utils/gsecLetterAccess';
import RecentDealsDateFilter from '../components/RecentDealsDateFilter';
import { useRecentDealsFilterDate } from '../hooks/useRecentDealsFilterDate';
import { filterDealsByFilterDate } from '../utils/recentDealsDate';

const FixedIncomeGsecPage = ({ allowedTabs, title }) => {
  // Brokerage is computed as: fee amount = faceValue * 0.015%
  // We store the fee amount (not the percentage), rounded to 4 decimals.
  const BROKERAGE_RATE = 0.00015; // 0.015%

  // Tint read-only / auto-calculated fields based on the chosen transaction type.
  // Buy -> green, Sell -> red, otherwise the legacy neutral amber look.
  const getDerivedFieldStyle = (txnType) => {
    if (txnType === 'Buy') {
      return {
        backgroundColor: '#dcfce7',
        border: '1.5px solid #16a34a',
        color: '#065f46',
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      };
    }
    if (txnType === 'Sell') {
      return {
        backgroundColor: '#fee2e2',
        border: '1.5px solid #dc2626',
        color: '#7f1d1d',
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
      };
    }
    return {
      backgroundColor: '#ffeeba',
      fontWeight: 'bold',
      transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
    };
  };

  // Modal for selling deals
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellDeals, setSellDeals] = useState([]);
  const [portfolioFromSellModal, setPortfolioFromSellModal] = useState(false);

  // Define the complete initial form state once
  const getInitialFormState = () => {
    // Get current date in YYYY-MM-DD format
    const today = new Date();
    const currentDate = today.toISOString().split('T')[0];
    
    // Check for reinvestment data from maturity handling
    const reinvestmentData = localStorage.getItem('reinvestmentDealData');
    let prePopulatedData = {};
    
    if (reinvestmentData) {
      try {
        const data = JSON.parse(reinvestmentData);
        prePopulatedData = {
          faceValue: data.principalAmount || '',
          // Add other pre-populated fields as needed
        };
        // Clear the reinvestment data after using it
        localStorage.removeItem('reinvestmentDealData');
      } catch (error) {
        console.error('Error parsing reinvestment data:', error);
      }
    }
    
    return {
      tradeDate: currentDate,
      tradeType: '',
      transactionType: '',
      counterparty: '',
      dealNumber: '',
      isin: '',
      faceValue: prePopulatedData.faceValue || '',
      valueDate: currentDate,
      nextCouponDate: '',
      lastCouponDate: '',
      numberOfDaysInterestAccrued: '',
      numberOfDaysForCouponPeriod: '',
      accruedInterest: '',
      accruedInterestBase: '',
      couponInterest: '',
      cleanPrice: '',
      dirtyPrice: '',
      couponDate1: '',
      couponDate2: '',
      issueDate: '',
      maturityDate: '',
      couponRate: '',
      yieldRate: '',
      cleanPriceBase: '',
      accruedInterestCalculation: '',
      broker: '',
      accruedInterestSixDecimals: '',
      accruedInterestFor100: '',
      settlementAmount: '',
      settlementMode: '',
      // Default trading defaults requested by ops (fund movement Yes,
      // Sherwood portfolio + Sherwood (trading) strategy, Seylan Bank
      // custodian, Seylan Bank settlement mode auto-picked once loaded).
      fundMovement: 'yes',
      couponDates: '',
      yield: '',
      brokerage: '',
      currency: 'LKR',
      portfolio: 'Sherwood',
      strategy: 'Sherwood',
      accruedInterestAdjustment: '',
      cleanPriceAdjustment: '',
      custodian: 'Seylan Bank'
    };
  };

  // Main form state (must be first!)
  const [form, setForm] = useState(getInitialFormState());
  const [createdGsecTransactionId, setCreatedGsecTransactionId] = useState(null);
  const [showGsecDocumentSection, setShowGsecDocumentSection] = useState(false);
  const [selectedTransactionForDocuments, setSelectedTransactionForDocuments] = useState(null);

  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user'));
  // Support all authorizer roles for multi-level auth
  const AUTHORIZER_ROLES = ['front_office', 'back_office_verifier', 'back_office_final'];
  const isAuthorizer = user && AUTHORIZER_ROLES.includes(user.role);
  const loggedInUsername = user?.username || '';
  const userRole = user?.role || '';

  // State for dropdowns and fields
  const custodianOptions = [
    { value: 'Sampath Bank', label: 'Sampath Bank' },
    { value: 'Seylan Bank', label: 'Seylan Bank' },
    { value: 'DFCC Bank', label: 'DFCC Bank' },
    { value: 'Pan Asia  Bank', label: 'Pan Asia Bank' },
    { value: 'Bank of Ceylon', label: 'Bank of Ceylon' },
    { value: 'Commercial Bank', label: 'Commercial Bank' },
    { value: 'NDB Bank', label: 'NDB Bank' },
    { value: 'Hatton National Bank', label: 'Hatton National Bank' },
    { value: 'Nations Trust Bank', label: 'Nations Trust Bank' }


  ];
  const [isinList, setIsinList] = useState([]);
  const [isinSearch, setIsinSearch] = useState('');
  const [counterparties, setCounterparties] = useState([]);

  // Fetch counterparties on mount
  useEffect(() => {
    counterpartyApi.getAll().then(data => {
      console.log('[DEBUG] Fetched counterparties:', data);
      setCounterparties(data);
    });
  }, []);
  const [brokers, setBrokers] = useState([]); // <-- Brokers state
  const [portfolioOptions, setPortfolioOptions] = useState([]); // Portfolio master
  const [strategies, setStrategies] = useState([]); // Strategy master
  const [settlementModes, setSettlementModes] = useState([]); // [{ payment_method, bank_payment_code }]
  const [selectedBankDetails, setSelectedBankDetails] = useState({ bank_name: '', bank_account_number: '', bank_branch: '' });
  // Loading states for dropdowns
  const [loadingIsins, setLoadingIsins] = useState(false);
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);
  const [loadingBrokers, setLoadingBrokers] = useState(false);

  // Fetch brokers, portfolios, strategies, settlement modes on mount
  useEffect(() => {
    setLoadingBrokers(true);
    axios.get(`${API_BASE_URL}/brokers`).then(res => {
      console.log('[DEBUG] Broker API response:', res.data);
      setBrokers(res.data);
    }).catch(() => setBrokers([])).finally(() => setLoadingBrokers(false));

    // Fetch portfolios
    axios.get(`${API_BASE_URL}/portfolio-master`).then(res => {
      const portfolios = res.data.map(p => ({ id: p.portfolio_id, name: p.portfolio_name }));
      setPortfolioOptions(portfolios);

      // Default to Sherwood: prefer literal id "Sherwood", else any portfolio
      // whose id or name contains "sherwood" (case-insensitive).
      if (!form.portfolio || form.portfolio === 'Sherwood') {
        const literal = portfolios.find(p => String(p.id) === 'Sherwood');
        const fuzzy = literal ||
          portfolios.find(p => `${p.id || ''} ${p.name || ''}`.toLowerCase().includes('sherwood'));
        if (fuzzy) {
          setForm(prev => ({ ...prev, portfolio: fuzzy.id }));
        }
      }
    }).catch(() => setPortfolioOptions([]));

    // Fetch strategies
    axios.get(`${API_BASE_URL}/strategy-master`).then(res => {
      const strategyOptions = res.data.map(s => ({
        id: s.strategy_id,
        name: s.strategy_id + (s.strategy_type ? ` (${s.strategy_type})` : '')
      }));
      setStrategies(strategyOptions);

      // Default to Sherwood (trading): prefer literal id "Sherwood", else any
      // strategy whose label contains "sherwood".
      if (!form.strategy || form.strategy === 'Sherwood') {
        const literal = strategyOptions.find(s => String(s.id) === 'Sherwood');
        const fuzzy = literal ||
          strategyOptions.find(s => `${s.id || ''} ${s.name || ''}`.toLowerCase().includes('sherwood'));
        if (fuzzy) {
          setForm(prev => ({ ...prev, strategy: fuzzy.id }));
        }
      }
    }).catch(() => setStrategies([]));

    // Fetch settlement modes (payment methods + codes)
    fetchSettlementModes()
      .then(data => {
        const modes = Array.isArray(data) ? data : [];
        setSettlementModes(modes);

        // Default settlement mode: any mode whose owner/label mentions
        // "Seylan Bank". Falls back to the legacy CBSL/Seylan1 picks so
        // existing setups keep working.
        if (!form.settlementMode && modes.length > 0) {
          const containsSeylanBank = (m) => {
            const owner = (m.payment_method_owner || m.owner || m.bank_name || m.bank || '').toLowerCase();
            const method = (m.payment_method || '').toLowerCase();
            const code = (m.bank_payment_code || '').toLowerCase();
            return (
              owner.includes('seylan bank') ||
              method.includes('seylan bank') ||
              code.includes('seylan bank')
            );
          };
          const defaultMode =
            modes.find(containsSeylanBank) ||
            modes.find(m => (m.bank_payment_code || '').toLowerCase().includes('seylan1')) ||
            modes.find(m => (m.payment_method || '').toLowerCase().includes('cbsl')) ||
            modes[0];

          if (defaultMode && defaultMode.bank_payment_code) {
            const code = defaultMode.bank_payment_code;
            // Update form with default settlement mode
            setForm(prev => ({ ...prev, settlementMode: code }));
            // Also fetch and populate bank details so the read-only fields are filled
            fetchBankDetailsByCode(code)
              .then(details => {
                if (details) {
                  setSelectedBankDetails({
                    bank_name: details.bank_name || '',
                    bank_account_number: details.bank_account_number || '',
                    bank_branch: details.bank_branch || ''
                  });
                } else {
                  setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
                }
              })
              .catch(() => {
                setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
              });
          }
        }
      })
      .catch(() => setSettlementModes([]));
  }, []);

  // Helper to check if user can authorize at this level (3-tier: front_office -> back_office_verifier -> back_office_final)
  function canAuthorize(tx) {
    if (tx.status !== 'pending') return false;
    const level = tx.current_approval_level || 'front_office';
    return (
      (userRole === 'front_office' && level === 'front_office') ||
      (userRole === 'back_office_verifier' && level === 'back_office_verifier') ||
      (userRole === 'back_office_final' && level === 'back_office_final')
    );
  }

  // State for transactions list
  // --- Auto-recalculate coupon dates and accrued interest when required fields are present ---
  useEffect(() => {
    console.log('[DEBUG] Auto-calculation useEffect triggered with:', {
      isin: form.isin,
      valueDate: form.valueDate,
      faceValue: form.faceValue,
      couponRate: form.couponRate
    });

    // Only trigger if we have ISIN and value date
    if (form.isin && form.valueDate) {
      // Add a small delay to ensure form state is fully updated
      const timeoutId = setTimeout(() => {
        fetchAndSetCouponDates(form.isin, form.valueDate, form.couponRate, form.faceValue);
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [form.isin, form.valueDate, form.faceValue, form.couponRate]);

  // Auto-calculate brokerage fee amount when broker and faceValue are present.
  useEffect(() => {
    const brokerSelected = Boolean(form.broker);
    const faceValueNum = (() => {
      const raw = form.faceValue ?? '';
      const normalized = String(raw).replace(/,/g, '');
      const n = parseFloat(normalized);
      return Number.isFinite(n) ? n : NaN;
    })();

    const nextBrokerage =
      brokerSelected && Number.isFinite(faceValueNum) && faceValueNum > 0
        ? (faceValueNum * BROKERAGE_RATE).toFixed(4)
        : '';

    // Prevent unnecessary state updates.
    if (String(form.brokerage ?? '') !== String(nextBrokerage)) {
      setForm(prev => ({ ...prev, brokerage: nextBrokerage }));
    }
  }, [form.broker, form.faceValue, form.brokerage]);

  const [recentGsecTransactions, setRecentGsecTransactions] = useState([]);
  const { filterDateYmd: recentDealsFilterDate, setFilterDateYmd: setRecentDealsFilterDate, loading: recentDealsFilterLoading } = useRecentDealsFilterDate();

  // State for warning messages
  const [warning, setWarning] = useState('');

  // Dedicated function to fetch coupon dates and calculate all related values
  const fetchAndSetCouponDates = async (isin, valueDate, couponRate, faceValue) => {
    setWarning(''); // Reset warning
    console.log('[DEBUG] Fetching coupon dates with:', { isin, valueDate, couponRate, faceValue });

    if (!isin || !valueDate) {
      setWarning('ISIN and Value Date are required to fetch coupon dates.');
      return;
    }

    // If we don't have couponRate or faceValue, skip the calculation
    if (!couponRate || !faceValue) {
      console.log('[DEBUG] Missing couponRate or faceValue, skipping calculation');
      return;
    }

    try {
      console.log('[DEBUG] Making API call to:', `${API_BASE_URL}/isin-master/${isin}/coupon-dates`);
      const response = await axios.get(`${API_BASE_URL}/isin-master/${isin}/coupon-dates`, {
        params: { valueDate }
      });

      console.log('[DEBUG] API coupon-dates response:', response.data);

      function forceDay15(dateStr) {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        const year = d.getFullYear();
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        return `${year}-${month}-15`;
      }

      let previousCouponDate = '';
      let nextCouponDate = '';

      if (response.data) {
        // Handle different response formats
        if (response.data.data) {
          previousCouponDate = response.data.data.previousCouponDate || '';
          nextCouponDate = response.data.data.nextCouponDate || '';
        } else if (response.data.previousCouponDate) {
          previousCouponDate = response.data.previousCouponDate;
          nextCouponDate = response.data.nextCouponDate;
        }

        console.log('[DEBUG] Extracted dates:', { previousCouponDate, nextCouponDate });

        const lastCoupon = forceDay15(previousCouponDate);
        const nextCoupon = forceDay15(nextCouponDate);

        console.log('[DEBUG] Processed dates:', { lastCoupon, nextCoupon });

        // Calculate all values
        let numberOfDaysInterestAccrued = '';
        if (lastCoupon && valueDate) {
          const d1 = new Date(lastCoupon);
          const d2 = new Date(valueDate);
          const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
          numberOfDaysInterestAccrued = !isNaN(diff) && diff >= 0 ? diff.toString() : '';
        }

        let numberOfDaysForCouponPeriod = '';
        if (lastCoupon && nextCoupon) {
          const d1 = new Date(lastCoupon);
          const d2 = new Date(nextCoupon);
          const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
          numberOfDaysForCouponPeriod = !isNaN(diff) && diff > 0 ? diff.toString() : '';
        }

        // Coupon interest per period (semi-annual): faceValue * (couponRate / 100) / 2
        let couponInterest = '';
        if (faceValue && couponRate && !isNaN(Number(faceValue)) && !isNaN(Number(couponRate))) {
          couponInterest = (Number(faceValue) * Number(couponRate) / 100 / 2).toFixed(4);
        }

        let accruedInterestCalculation = '';
        if (couponInterest && numberOfDaysInterestAccrued && numberOfDaysForCouponPeriod &&
          !isNaN(Number(couponInterest)) && !isNaN(Number(numberOfDaysInterestAccrued)) &&
          !isNaN(Number(numberOfDaysForCouponPeriod)) && Number(numberOfDaysForCouponPeriod) !== 0) {
          const ci = Number(couponInterest);
          const nda = Number(numberOfDaysInterestAccrued);
          const ndcp = Number(numberOfDaysForCouponPeriod);
          accruedInterestCalculation = (ci * (nda / ndcp)).toFixed(9);
        }

        let accruedInterestSixDecimals = '';
        if (couponInterest && numberOfDaysInterestAccrued && numberOfDaysForCouponPeriod &&
          !isNaN(Number(couponInterest)) && !isNaN(Number(numberOfDaysInterestAccrued)) &&
          !isNaN(Number(numberOfDaysForCouponPeriod)) && Number(numberOfDaysForCouponPeriod) !== 0) {
          const ci = Number(couponInterest);
          const nda = Number(numberOfDaysInterestAccrued);
          const ndcp = Number(numberOfDaysForCouponPeriod);
          const sixDec = (ci * (nda / ndcp));
          accruedInterestSixDecimals = sixDec.toFixed(6);
        }

        // Per-100 accrued interest from the coupon-period day-count formula.
        // This is the authoritative per-100 figure used for the dirty/clean price
        // build-up and for deriving the money accrued (per senior-accountant convention):
        //   accruedInterestPer100 = (CouponPerPeriod / FV * 100) * (NDA / NDCP)
        //                         = (CouponRate% / 2) * (NDA / NDCP)
        let accruedInterestPer100 = '';
        if (couponInterest && numberOfDaysInterestAccrued && numberOfDaysForCouponPeriod &&
          !isNaN(Number(couponInterest)) && !isNaN(Number(numberOfDaysInterestAccrued)) &&
          !isNaN(Number(numberOfDaysForCouponPeriod)) && Number(numberOfDaysForCouponPeriod) !== 0 &&
          faceValue && !isNaN(Number(faceValue)) && Number(faceValue) > 0) {
          const ci = Number(couponInterest);
          const nda = Number(numberOfDaysInterestAccrued);
          const ndcp = Number(numberOfDaysForCouponPeriod);
          const fv = Number(faceValue);
          // ci is the money coupon for the period; convert to per-100 then apply day-count fraction.
          const per100Raw = (ci / fv * 100) * (nda / ndcp);
          accruedInterestPer100 = (Math.round(per100Raw * 10000) / 10000).toFixed(4);
        }

        // Money-side accrued base = (Accrued per 100) * FaceValue / 100.
        // This is the "Treasury Bonds vs Accrued Interest" split the senior accounting team uses.
        let accruedInterestBase = '';
        if (faceValue && accruedInterestPer100 !== '') {
          const fv = Number(faceValue);
          const per100 = Number(accruedInterestPer100);
          if (!isNaN(fv) && !isNaN(per100)) {
            accruedInterestBase = ((per100 * fv) / 100).toFixed(6);
          }
        }

        // Legacy "for 100" field kept for backward-compatibility with stored / displayed values.
        // Now mirrors the money base so persisted rows remain self-consistent.
        const accruedInterestFor100 = accruedInterestBase;

        console.log('[DEBUG] Calculated values:', {
          lastCoupon,
          nextCoupon,
          numberOfDaysInterestAccrued,
          numberOfDaysForCouponPeriod,
          couponInterest,
          accruedInterestPer100,
          accruedInterestSixDecimals,
          accruedInterestBase
        });

        // Update form state with all calculated values.
        // accruedInterest (money) = base + adjustment (where adjustment is now in LKR money terms).
        setForm(prevForm => {
          const accruedInterestAdj = parseFloat(prevForm.accruedInterestAdjustment) || 0;
          const accruedInterestFinal = ((parseFloat(accruedInterestBase) || 0) + accruedInterestAdj).toFixed(4);

          const newForm = {
            ...prevForm,
            lastCouponDate: lastCoupon,
            nextCouponDate: nextCoupon,
            numberOfDaysInterestAccrued,
            numberOfDaysForCouponPeriod,
            couponInterest,
            accruedInterestCalculation,
            accruedInterestSixDecimals,
            accruedInterestFor100,
            accruedInterestBase,
            accruedInterestPer100,
            accruedInterest: accruedInterestFinal
          };

          console.log('[DEBUG] Updated form state after coupon dates:', newForm);
          return newForm;
        });

      } else {
        console.log('[DEBUG] No data returned from API');
        setWarning('No data returned from coupon date API.');
      }

    } catch (err) {
      console.error('[DEBUG] Error fetching coupon dates:', err);
      console.error('[DEBUG] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });

      setWarning(`Error fetching coupon dates: ${err.message}`);

      // Don't clear the form on error, just show warning
    }
  };

  // Removed duplicate useEffect - now handled by the comprehensive one above

  // State for authorization and UI
  const [comment, setComment] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formKey, setFormKey] = useState(0); // Key to force form re-render
  const [retryCount, setRetryCount] = useState(0); // Track retry attempts
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent duplicate submissions
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false); // Flag to prevent duplicate saves when viewing
  const [editingTransactionId, setEditingTransactionId] = useState(null); // Track which transaction is being edited
  const [isEditMode, setIsEditMode] = useState(false); // Flag to track if we're in edit mode (prevents auto-submit)
  const [editError, setEditError] = useState('');
  const [tradeDateHolidayError, setTradeDateHolidayError] = useState('');
  const [valueDateHolidayError, setValueDateHolidayError] = useState('');
  const [originalEditValues, setOriginalEditValues] = useState({ faceValue: null, yield: null }); // Track original values when editing
  const [isinRequiresReselection, setIsinRequiresReselection] = useState(false); // Flag to indicate ISIN needs reselection
  const [editingTransaction, setEditingTransaction] = useState(null); // Store the full transaction object being edited

  // Monitor loading state changes (optimized)
  useEffect(() => {
    if (isLoading) {
      console.log('=== LOADING STARTED ===');
    } else {
      console.log('=== LOADING STOPPED ===');
    }
  }, [isLoading]);

  const filteredIsinList = useMemo(() => {
    const q = isinSearch.trim().toLowerCase();
    if (!q) return isinList;
    return isinList.filter((isin) => {
      const num = String(isin?.isin_number || '').toLowerCase();
      const issuer = String(isin?.isin_issuer || '').toLowerCase();
      return num.includes(q) || issuer.includes(q);
    });
  }, [isinList, isinSearch]);

  // Memoized dropdown options for performance
  const memoIsins = useMemo(
    () =>
      filteredIsinList.map((isin) => (
        <option key={isin.isin_number} value={isin.isin_number}>
          {isin.isin_number} - {isin.isin_issuer}
        </option>
      )),
    [filteredIsinList]
  );

  const memoCounterparties = useMemo(() => {
    // Group counterparties by type
    const individual = counterparties.filter(cp => cp.type === 'individual');
    const joint = counterparties.filter(cp => cp.type === 'joint');
    const corporate = counterparties.filter(cp => cp.type === 'corporate');
    
    return (
      <>
        {individual.length > 0 && (
          <optgroup label="Individual" className="counterparty-individual">
            {individual.map(cp => (
              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-individual">
                {cp.short_name}
              </option>
            ))}
          </optgroup>
        )}
        {joint.length > 0 && (
          <optgroup label="Joint" className="counterparty-joint">
            {joint.map(cp => (
              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-joint">
                {cp.short_name}
              </option>
            ))}
          </optgroup>
        )}
        {corporate.length > 0 && (
          <optgroup label="Corporate" className="counterparty-corporate">
            {corporate.map(cp => (
              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-corporate">
                {cp.short_name}
              </option>
            ))}
          </optgroup>
        )}
      </>
    );
  }, [counterparties]);

  // Currency options
  const currencyOptions = [
    'USD', 'LKR', 'EUR', 'GBP', 'JPY', 'INR', 'CNY', 'SGD', 'AUD', 'CAD', 'CHF', 'HKD', 'NZD', 'SEK', 'ZAR'
  ];

  // Calculate bond dirty price using Actual/Actual day count basis with precise semiannual t values
  // Always return clean/dirty price per 100 (not scaled by face value)
  const calculatePrices = useCallback(({ faceValue, couponRate, yieldRate, valueDate, maturityDate, issueDate, couponDate1, couponDate2 }) => {
    console.log("CALCULATE PRICES FUNCTION CALLED");
    if (!faceValue || !couponRate || !yieldRate || !valueDate || !maturityDate || !issueDate || !couponDate1 || !couponDate2) return { dirtyPrice: '', cleanPrice: '', accruedInterest: '' };

    // Fixed semi-annual frequency
    const frequency = 2;

    // Parse and normalize all date inputs
    // For price per 100, use fv = 100
    const fv = 100;
    const cr = parseFloat(couponRate) / 100; // Convert percentage to decimal
    const ytm = parseFloat(yieldRate) / 100; // Convert percentage to decimal
    // Use local date parsing to avoid timezone-induced day shifts (JS treats YYYY-MM-DD as UTC).
    const settle = parseLocalYMD(valueDate);
    const maturity = parseLocalYMD(maturityDate);

    // Calculate coupon amount per period (per 100)
    const coupon = fv * cr / frequency;
    const ytmPerPeriod = ytm / frequency;
    const monthsPerPeriod = 12 / frequency;  // 6 months for semi-annual

    // Step 1: Coupon period containing settlement — same as Excel PRICE (walk back from maturity)
    const { lastCoupon, nextCoupon } = findCouponPeriodFromMaturity(settle, maturity, frequency);

    // Calculate exact days in current coupon period using Actual/Actual day count
    const daysInPeriod = getDaysDifference(nextCoupon, lastCoupon);

    // Days from value date to next coupon
    const daysToNextCoupon = getDaysDifference(nextCoupon, settle);

    // Calculate precise fractional period t for first payment
    const fractionalPeriod = daysToNextCoupon / daysInPeriod;

    // Step 2: Generate all future cash flows from next coupon to maturity
    const cashFlows = [];
    let cfDate = new Date(nextCoupon);
    let periodCount = 0;

    while (cfDate <= maturity) {
      const isFinal = cfDate.getTime() === maturity.getTime();
      const amount = isFinal ? coupon + fv : coupon;
      cashFlows.push({
        date: new Date(cfDate),
        amount,
        periodCount
      });
      cfDate.setMonth(cfDate.getMonth() + monthsPerPeriod);
      periodCount++;
    }

    // Debug logging for bonds near maturity - cash flows
    const daysToMaturityForDebug = getDaysDifference(maturity, settle);
    if (daysToMaturityForDebug < 180) { // Less than 6 months
      console.log('=== CASH FLOWS DEBUG ===');
      console.log('Number of cash flows:', cashFlows.length);
      cashFlows.forEach((cf, index) => {
        console.log(`Cash flow ${index + 1}:`, {
          date: cf.date.toISOString().split('T')[0],
          amount: cf.amount,
          periodCount: cf.periodCount,
          isFinal: cf.date.getTime() === maturity.getTime()
        });
      });
      console.log('========================');
    }

    // Step 3: Calculate PV of each cash flow using Actual/Actual day count basis and precise t values
    const results = cashFlows.map((cf) => {
      // Calculate precise t = fractionalPeriod + integer number of periods
      const t = fractionalPeriod + cf.periodCount;

      // Calculate present value using the precise t value and semi-annual discounting
      const pv = cf.amount / Math.pow(1 + ytmPerPeriod, t);

      return {
        date: formatDate(cf.date),
        amount: cf.amount.toFixed(4),
        t: t.toFixed(4),
        presentValue: pv.toFixed(4)
      };
    });

    // Calculate the dirty price by summing all present values
    let dirtyPrice = 0;
    for (const r of results) {
      dirtyPrice += parseFloat(r.presentValue);
    }

    // Fix for floating point precision issues before truncating
    // Multiply by 10000, add a small epsilon to ensure proper truncation, then truncate
    const dirtyPrice10000 = Math.floor(dirtyPrice * 10000 );
    const truncatedDirtyPrice = dirtyPrice10000 / 10000;

    // Calculate accrued interest (per 100)
    const daysAccrued = getDaysDifference(settle, lastCoupon);
    const accruedInterest = coupon * (daysAccrued / daysInPeriod);
    // Since fv is always 100 above, accruedInterest is already per 100
    // Round accrued interest to 4 decimal places (not truncated)
    const roundedAccruedInterestPer100 = Math.round(accruedInterest * 10000) / 10000;

    // Calculate clean price using Excel PRICE function (Actual/Actual basis)
    // Excel PRICE: settlement, maturity, rate, yld, redemption, frequency, basis
    const excelCleanPrice = excelPRICE(
      settle,           // settlement (value date)
      maturity,         // maturity date
      cr,               // coupon rate (decimal)
      ytm,              // yield (decimal)
      100,              // redemption value per 100
      frequency,        // frequency (2 = semi-annual)
      1                 // basis (1 = Actual/Actual)
    );

    // Use Excel PRICE clean price if valid, otherwise fall back to dirty price - accrued interest
    let roundedCleanPrice;
    if (!isNaN(excelCleanPrice) && isFinite(excelCleanPrice)) {
      // Round clean price to 4 decimal places
      roundedCleanPrice = Math.round(excelCleanPrice * 10000) / 10000;
      console.log('Excel PRICE clean price:', excelCleanPrice, 'rounded:', roundedCleanPrice);
    } else {
      // Fallback: Clean price is dirty price minus accrued interest (all per 100)
      const cleanPrice = truncatedDirtyPrice - roundedAccruedInterestPer100;
      roundedCleanPrice = Math.round(cleanPrice * 10000) / 10000;
      console.warn('Excel PRICE returned invalid value, using fallback calculation');
    }

    // Calculate dirty price = Excel Clean Price (rounded) + Accrued Interest (rounded)
    // Both clean price and accrued interest are rounded to 4 decimals.
    // Use Math.round instead of Math.floor because both addends are already rounded
    // to 4dp; any sub-unit deviation is purely floating-point noise
    // (e.g. 98.2237 + 3.5735 = 101.79719999… in IEEE 754).
    const dirtyPriceFromExcel = roundedCleanPrice + roundedAccruedInterestPer100;
    const truncatedDirtyPriceFromExcel = Math.round(dirtyPriceFromExcel * 10000) / 10000;

    // Debug logging for bonds near maturity
    const daysToMaturityForDebug2 = getDaysDifference(maturity, settle);
    if (daysToMaturityForDebug2 < 180) { // Less than 6 months
      console.log('=== BOND NEAR MATURITY DEBUG ===');
      console.log('Days to maturity:', daysToMaturityForDebug2);
      console.log('Value date:', settle.toISOString().split('T')[0]);
      console.log('Maturity date:', maturity.toISOString().split('T')[0]);
      console.log('Last coupon:', lastCoupon.toISOString().split('T')[0]);
      console.log('Next coupon:', nextCoupon.toISOString().split('T')[0]);
      console.log('Days accrued:', daysAccrued);
      console.log('Days in period:', daysInPeriod);
      console.log('Coupon rate:', couponRate);
      console.log('Yield rate:', yieldRate);
      console.log('Coupon amount:', coupon);
      console.log('Accrued interest (raw):', accruedInterest);
      console.log('Accrued interest (rounded):', roundedAccruedInterestPer100);
      console.log('Dirty price (raw NPV):', dirtyPrice);
      console.log('Dirty price (truncated NPV):', truncatedDirtyPrice);
      console.log('Excel PRICE clean price (raw):', excelCleanPrice);
      console.log('Excel PRICE clean price (rounded):', roundedCleanPrice);
      console.log('Accrued interest (rounded):', roundedAccruedInterestPer100);
      console.log('Dirty price = Excel Clean Price + Accrued Interest:', truncatedDirtyPriceFromExcel);
      console.log('================================');
    }

    // Format all values as strings with exactly 4 decimal places
    // Dirty price = Excel Clean Price (rounded) + Accrued Interest (rounded)
    const formattedAccruedInterest = roundedAccruedInterestPer100.toFixed(4);
    const formattedCleanPrice = roundedCleanPrice.toFixed(4);
    const formattedDirtyPrice = truncatedDirtyPriceFromExcel.toFixed(4);

    function generateFullCouponSchedule(issueDate, maturityDate, couponDate1, couponDate2) {
      if (!issueDate || !maturityDate || !couponDate1 || !couponDate2) return [];
      const schedule = [];
      const start = new Date(issueDate);
      const end = new Date(maturityDate);
      let year = start.getFullYear();
      // Parse coupon MM-DD from couponDate1/couponDate2 (e.g., '04-15')
      const [m1, d1] = couponDate1.split('-').map(Number);
      const [m2, d2] = couponDate2.split('-').map(Number);
      while (true) {
        const c1 = new Date(year, m1 - 1, d1);
        const c2 = new Date(year, m2 - 1, d2);
        if (c1 >= start && c1 <= end) schedule.push(new Date(c1));
        if (c2 >= start && c2 <= end) schedule.push(new Date(c2));
        if (c1 > end && c2 > end) break;
        year++;
      }
      schedule.sort((a, b) => a - b);
      return schedule;
    }

    function getLastAndNextCouponDates(schedule, valueDate) {
      const vDate = new Date(valueDate);
      let last = null, next = null;
      for (let i = 0; i < schedule.length; i++) {
        if (schedule[i] <= vDate) last = schedule[i];
        if (schedule[i] > vDate) { next = schedule[i]; break; }
      }
      return { lastCouponDate: last, nextCouponDate: next };
    }

    // Helper function to get days difference between two dates (for Actual/Actual day count)
    function getDaysDifference(date1, date2) {
      const millisecondsPerDay = 24 * 60 * 60 * 1000;
      return Math.round(Math.abs((date1 - date2) / millisecondsPerDay));
    }

    // Helper function to format dates to YYYY-MM-DD
    function formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    // --- END Coupon Schedule and Date Utilities ---

    // Return all values formatted consistently, always per 100
    return {
      dirtyPrice: formattedDirtyPrice,
      cleanPrice: formattedCleanPrice,
      accruedInterestPer100: formattedAccruedInterest
    };
  }, []);

  // Keep settlement amount aligned with the exact 4-decimal dirty price shown in UI.
  const calculateSettlementAmountFromDirty = useCallback((faceValue, dirtyPrice) => {
    const faceValueNum = parseFloat(faceValue);
    const dirtyPriceNum = parseFloat(dirtyPrice);
    if (!Number.isFinite(faceValueNum) || !Number.isFinite(dirtyPriceNum)) return '';
    const uiDirtyPrice = Number(dirtyPriceNum.toFixed(4));
    return (faceValueNum * uiDirtyPrice / 100).toFixed(4);
  }, []);

  // --- Dirty/Clean Price Calculation Effect ---
  useEffect(() => {
    // In view-only mode, preserve values from the selected transaction; skip recalculation
    if (isViewMode) {
      return;
    }
    // Use all required fields for calculation
    const requiredFields = [
      form.faceValue,
      form.couponRate,
      form.yield || form.yieldRate,
      form.valueDate,
      form.maturityDate,
      form.issueDate,
      form.couponDate1,
      form.couponDate2
    ];
    if (requiredFields.every(Boolean)) {
      const params = {
        faceValue: form.faceValue,
        couponRate: form.couponRate,
        yieldRate: form.yield || form.yieldRate,
        valueDate: form.valueDate,
        maturityDate: form.maturityDate,
        issueDate: form.issueDate,
        couponDate1: form.couponDate1,
        couponDate2: form.couponDate2
      };
      console.log('CALC PARAMS', params);
      const result = calculatePrices(params);
      console.log('CALC RESULT', result);

      // Calculate settlement amount: Face Value * Dirty Price/100
      const settlementAmount = calculateSettlementAmountFromDirty(form.faceValue, result.dirtyPrice);

      // Derive money accrued interest from the per-100 result so the persisted figure
      // follows the senior-accounting convention:
      //   Accrued Interest (money) = (Accrued per 100) * Face Value / 100  +  Adjustment (money)
      const faceValueNum = parseFloat(form.faceValue);
      const per100Num = parseFloat(result.accruedInterestPer100 || '');
      let accruedInterestMoney = '';
      if (Number.isFinite(faceValueNum) && Number.isFinite(per100Num)) {
        const adjMoney = parseFloat(form.accruedInterestAdjustment) || 0;
        const money = (per100Num * faceValueNum) / 100 + adjMoney;
        accruedInterestMoney = (Math.round(money * 10000) / 10000).toFixed(4);
      }

      // Only settlementAmount uses faceValue; prices shown are always per 100
      setForm(f => ({
        ...f,
        dirtyPrice: result.dirtyPrice || '',
        cleanPrice: result.cleanPrice || '',
        cleanPriceBase: result.cleanPrice || '',
        accruedInterestPer100: result.accruedInterestPer100 || '',
        accruedInterest: accruedInterestMoney || f.accruedInterest || '',
        settlementAmount: settlementAmount || ''
      }));
    } else {
      setForm(f => ({ ...f, dirtyPrice: '', cleanPrice: '', accruedInterestPer100: '', settlementAmount: '' }));
    }
  }, [form.faceValue, form.couponRate, form.yield, form.yieldRate, form.valueDate, form.maturityDate, form.issueDate, form.couponDate1, form.couponDate2, calculatePrices, calculateSettlementAmountFromDirty, isViewMode]);

  // Helper for caching with expiry
  function getCachedOrFetch(key, fetchFn, setState, setLoading, fallback = [], expiryMin = 60) {
    setLoading(true);
    const cache = localStorage.getItem(key);
    if (cache) {
      const { data, timestamp } = JSON.parse(cache);
      if (Date.now() - timestamp < expiryMin * 60 * 1000) {
        setState(data);
        setLoading(false);
        return;
      }
    }
    fetchFn()
      .then(data => {
        setState(data);
        localStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
      })
      .catch(err => {
        console.error(`Error fetching ${key}:`, err);
        setState(fallback);
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    console.log('[DEBUG] Fetching ISINs and counterparties...');
    // Direct API calls without caching to ensure fresh data
    console.log('[DEBUG] Directly fetching ISINs...');
    setLoadingIsins(true);
    axios.get(`${API_BASE_URL}/isin-master`)
      .then(res => {
        console.log('[DEBUG] ISIN API response:', res.data);
        const isinData = res.data.data || [];
        setIsinList(isinData);
        console.log('[DEBUG] ISINs loaded:', isinData.length);
      })
      .catch(err => {
        console.error('Error fetching ISINs:', err);
        setIsinList([]);
      })
      .finally(() => setLoadingIsins(false));

    console.log('[DEBUG] Directly fetching counterparties...');
    setLoadingCounterparties(true);
    axios.get(`${API_BASE_URL}/counterparties`)
      .then(res => {
        console.log('[DEBUG] Counterparty API response:', res.data);
        const counterpartyData = res.data.data || [];
        setCounterparties(counterpartyData);
        console.log('[DEBUG] Counterparties loaded:', counterpartyData.length);
      })
      .catch(err => {
        console.error('Error fetching counterparties:', err);
        setCounterparties([]);
      })
      .finally(() => setLoadingCounterparties(false));
    fetchRecentGsecTransactions();
  }, []);

  // Auto-calculate prices when relevant fields change
  // (Removed duplicate auto-calculate prices useEffect to prevent hook order issues)


  const handleChange = useCallback((e) => {
    // Clear view mode when user starts editing the form
    if (isViewMode) {
      setIsViewMode(false);
    }
    const { name, value } = e.target;

    // Reject out-of-range / malformed dates (e.g. a 5-digit year) so they never
    // reach form state. Bounds are also enforced via the input's min/max attrs.
    if ((name === 'tradeDate' || name === 'valueDate') && !isDateInRange(value)) {
      return;
    }
    
    // Holiday validation for tradeDate and valueDate
    if (name === 'tradeDate' || name === 'valueDate') {
      setForm(prev => ({ ...prev, [name]: value }));
      
      // Validate holiday asynchronously
      if (value) {
        const currency = form.currency || 'LKR';
        checkHolidayForCurrency(value, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            const message = formatHolidayMessage(holidayCheck, name === 'tradeDate' ? 'Trade Date' : 'Value Date');
            if (name === 'tradeDate') {
              setTradeDateHolidayError(message);
            } else {
              setValueDateHolidayError(message);
            }
          } else {
            if (name === 'tradeDate') {
              setTradeDateHolidayError('');
            } else {
              setValueDateHolidayError('');
            }
          }
        });
      } else {
        // Clear error if date is cleared
        if (name === 'tradeDate') {
          setTradeDateHolidayError('');
        } else {
          setValueDateHolidayError('');
        }
      }
      return;
    }
    
    // Re-validate dates when currency changes
    if (name === 'currency') {
      setForm(prev => ({ ...prev, currency: value }));
      
      // Re-validate both dates with new currency
      const currency = value || 'LKR';
      if (form.tradeDate) {
        checkHolidayForCurrency(form.tradeDate, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            setTradeDateHolidayError(formatHolidayMessage(holidayCheck, 'Trade Date'));
          } else {
            setTradeDateHolidayError('');
          }
        });
      }
      if (form.valueDate) {
        checkHolidayForCurrency(form.valueDate, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            setValueDateHolidayError(formatHolidayMessage(holidayCheck, 'Value Date'));
          } else {
            setValueDateHolidayError('');
          }
        });
      }
      return;
    }
    
    // If editing a rejected deal and face value or yield changes, require ISIN reselection
    if (isEditMode && editingTransactionId && (name === 'faceValue' || name === 'yield')) {
      const currentValue = value ? String(value).trim() : '';
      const originalValue = name === 'faceValue' 
        ? (originalEditValues.faceValue ? String(originalEditValues.faceValue).trim() : '')
        : (originalEditValues.yield ? String(originalEditValues.yield).trim() : '');
      
      // Normalize values for comparison (remove commas, compare as numbers if both are numeric)
      const currentNum = currentValue.replace(/,/g, '');
      const originalNum = originalValue.replace(/,/g, '');
      const hasChanged = currentNum !== originalNum && 
                        (currentNum !== '' && originalNum !== '') && 
                        (parseFloat(currentNum) !== parseFloat(originalNum));
      
      // Check if the value has actually changed
      if (hasChanged && form.isin) {
        // Clear ISIN and set flag requiring reselection
        setForm(prev => ({ ...prev, isin: '', [name]: value }));
        setIsinRequiresReselection(true);
        setError(`Face Value or Yield has changed. Please reselect the ISIN to recalculate prices.`);
        return;
      }
    }
    if (name === 'settlementMode') {
      // value is bank_payment_code
      setForm(prev => ({ ...prev, settlementMode: value }));
      // fetch and display bank details
      fetchBankDetailsByCode(value)
        .then(details => {
          if (details) {
            setSelectedBankDetails({
              bank_name: details.bank_name || '',
              bank_account_number: details.bank_account_number || '',
              bank_branch: details.bank_branch || ''
            });
          } else {
            setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
          }
        })
        .catch(() => setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' }));
      return;
    }
    
    // Clear ISIN if Face Value or Yield are cleared (validation requirement)
    if ((name === 'faceValue' || name === 'yield') && (!value || value.trim() === '') && form.isin) {
      setForm(prev => ({ ...prev, isin: '', [name]: value }));
      setError('ISIN cleared because Face Value and Yield are required before ISIN selection.');
      return;
    }
    
    // If transactionType changes to 'Sell', open modal
    if (name === 'transactionType' && value === 'Sell') {
      if (!form.isin) {
        setError('Please select an ISIN before selling.');
        setForm(prev => ({ ...prev, transactionType: '' }));
        return;
      }
      setShowSellModal(true);
      setForm(prev => ({ ...prev, transactionType: value }));
      return;
    }
    if (name === 'custodian') {
      setForm(form => ({ ...form, custodian: value }));
      return;
    }
    if (name === 'isin') {
      // Validation: Face Value and Yield must be filled before ISIN selection
      if (!form.faceValue || !form.yield) {
        setError('Please fill Face Value and Yield before selecting an ISIN.');
        return;
      }
      
      // If ISIN is being reselected after face value/yield change, clear the flag
      if (isinRequiresReselection) {
        setIsinRequiresReselection(false);
        setError(''); // Clear any previous error
      }
      
      // ISIN selection logic (autofill and coupon date fetch)
      const selected = isinList.find(isin => isin.isin_number === value);
      if (selected) {
        // Use global date utilities for all date parsing
        let issueDate = '';
        if (selected.issue_date && /^\d{4}-\d{2}-\d{2}$/.test(selected.issue_date)) {
          const d = parseLocalYMD(selected.issue_date);
          issueDate = d ? formatLocalYMD(d) : '';
        } else {
          issueDate = formatLocalYMD(selected.issue_date || selected.issueDate);
        }
        let maturityDate = '';
        if (selected.maturity_date && /^\d{4}-\d{2}-\d{2}$/.test(selected.maturity_date)) {
          const d = parseLocalYMD(selected.maturity_date);
          maturityDate = d ? formatLocalYMD(d) : '';
        } else {
          maturityDate = formatLocalYMD(selected.maturity_date || selected.maturityDate);
        }
        let couponDate1 = '';
        let couponDate2 = '';
        if (selected.coupon_date_1) {
          const d = new Date(selected.coupon_date_1);
          couponDate1 = `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
        }
        if (selected.coupon_date_2) {
          const d = new Date(selected.coupon_date_2);
          couponDate2 = `${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
        }
        // Always update form with selected ISIN data
        setForm(f => {
          const updatedForm = {
            ...f,
            isin: selected.isin_number,
            faceValue: f.faceValue || selected.face_value, // Keep existing face value if present
            couponRate: selected.coupon_rate || f.couponRate,
            issueDate,
            maturityDate,
            couponDate1,
            couponDate2
          };
          return updatedForm;
        });

        // Trigger calculation immediately after form update
        setTimeout(() => {
          const currentFaceValue = form.faceValue || selected.face_value;
          const currentCouponRate = selected.coupon_rate || form.couponRate;

          if (form.valueDate && currentFaceValue && currentCouponRate) {
            fetchAndSetCouponDates(
              selected.isin_number,
              form.valueDate,
              currentCouponRate,
              currentFaceValue
            );
          }
        }, 200); // Increased timeout to ensure state updates
        setSelectedIsinForCouponMonths(selected.isin_number);
        // (No need to recalculate here, handled above)
        const valueDate = form.valueDate;
        const faceValue = parseFloat(selected.face_value || form.faceValue) || 0;
        const couponRate = parseFloat(selected.coupon_rate || form.couponRate) || 0;
        const schedule = generateFullCouponSchedule(issueDate, maturityDate, couponDate1, couponDate2);
        const { lastCouponDate, nextCouponDate } = getLastAndNextCouponDates(schedule, valueDate);
        let numberOfDaysInterestAccrued = '';
        let numberOfDaysForCouponPeriod = '';
        let couponInterest = '';
        let accruedInterestCalculation = '';
        let accruedInterestSixDecimals = '';
        let accruedInterest = '';
        let accruedInterestBase = '';
        if (lastCouponDate && nextCouponDate) {
          numberOfDaysInterestAccrued = getDaysDifference(new Date(valueDate), lastCouponDate).toString();
          numberOfDaysForCouponPeriod = getDaysDifference(nextCouponDate, lastCouponDate).toString();
          couponInterest = (faceValue * (couponRate / 100) / 2).toFixed(4);
          accruedInterestCalculation = `${(Number(couponRate) / 2).toFixed(4)} * (${numberOfDaysInterestAccrued} / ${numberOfDaysForCouponPeriod})`;
          let aiRaw = (parseFloat(couponInterest) * (parseInt(numberOfDaysInterestAccrued) / parseInt(numberOfDaysForCouponPeriod)));
          accruedInterestSixDecimals = aiRaw.toFixed(6);
          let aiTrunc = Math.floor(aiRaw * 10000) / 10000;
          accruedInterest = aiTrunc.toFixed(4);
          accruedInterestBase = accruedInterest;
        }
        setForm(f => ({
          ...f,
          lastCouponDate: lastCouponDate ? formatDate(lastCouponDate) : '',
          nextCouponDate: nextCouponDate ? formatDate(nextCouponDate) : '',
          numberOfDaysInterestAccrued,
          numberOfDaysForCouponPeriod,
          couponInterest,
          accruedInterestCalculation,
          accruedInterestSixDecimals,
          accruedInterest,
          accruedInterestBase
        }));
      } else {
        setForm(form => ({ ...form, isin: value, issueDate: '', maturityDate: '', couponRate: '', couponDates: '' }));
        setSelectedIsinForCouponMonths('');
      }
    } else if (name === 'tradeDate') {
      setForm(form => ({ ...form, tradeDate: value }));
      if (value) {
        const datePart = value.replace(/-/g, '');
        setForm(form => ({ ...form, dealNumber: `${datePart}/GSEC/0001` }));
      } else {
        setForm(form => ({ ...form, dealNumber: '' }));
      }
    } else if (name === 'valueDate') {
      setForm(prevForm => ({ ...prevForm, valueDate: value }));
      if (form.isin && value) {
        setTimeout(() => {
          fetchAndSetCouponDates(form.isin, value, form.couponRate, form.faceValue);
        }, 100);
      } else {
        setForm(prevForm => ({
          ...prevForm,
          lastCouponDate: '',
          nextCouponDate: '',
          numberOfDaysInterestAccrued: '',
          numberOfDaysForCouponPeriod: '',
          accruedInterestCalculation: '',
          accruedInterestSixDecimals: '',
          accruedInterestFor100: '',
          accruedInterestBase: '',
          accruedInterest: ''
        }));
      }
    } else if (name === 'accruedInterestAdjustment') {
      // Adjustment is now expressed in LKR money (not per-100). To keep the dirty price
      // consistent we convert the money adjustment to the equivalent per-100 delta:
      //   adjPer100 = adjMoney / FaceValue * 100
      setForm(form => {
        const basePer100 = parseFloat(form.accruedInterestPer100) || 0;
        const fv = parseFloat(form.faceValue) || 0;
        const adjMoney = parseFloat(value) || 0;
        const adjPer100 = fv > 0 ? (adjMoney / fv) * 100 : 0;
        const rawPer100 = basePer100 + adjPer100;
        const roundedPer100 = Math.round(rawPer100 * 10000) / 10000;
        const cleanPrice = parseFloat(form.cleanPrice) || 0;
        const dirtyPrice = cleanPrice + roundedPer100;
        const normalizedDirtyPrice = dirtyPrice.toFixed(4);
        // Money accrued = base per100 * FV / 100 + adjustment money
        const accruedMoney = (basePer100 * fv) / 100 + adjMoney;
        const roundedAccruedMoney = Math.round(accruedMoney * 10000) / 10000;
        return {
          ...form,
          accruedInterestAdjustment: value,
          accruedInterest: roundedAccruedMoney.toFixed(4),
          accruedInterestPer100: roundedPer100.toFixed(4),
          dirtyPrice: normalizedDirtyPrice,
          settlementAmount: calculateSettlementAmountFromDirty(form.faceValue, normalizedDirtyPrice)
        };
      });
    } else if (name === 'cleanPriceAdjustment') {
      setForm(form => {
        // Use cleanPriceBase if available, otherwise use calculated cleanPrice as base
        const base = parseFloat(form.cleanPriceBase) || parseFloat(form.cleanPrice) || 0;
        const adj = value === '' ? 0 : parseFloat(value) || 0; // Allow empty string, treat as 0
        const rawCleanPrice = base + adj;
        // Round clean price to 4 decimal places
        const roundedCleanPrice = Math.round(rawCleanPrice * 10000) / 10000;
        // IMPORTANT: Use accruedInterestPer100 (per 100 basis) NOT accruedInterest (absolute value)
        // accruedInterest might be scaled by face value, which would cause huge differences
        const accruedInterestPer100 = parseFloat(form.accruedInterestPer100) || 0;
        const dirtyPrice = roundedCleanPrice + accruedInterestPer100;
        const normalizedDirtyPrice = dirtyPrice.toFixed(4);
        return {
          ...form,
          cleanPriceAdjustment: value,
          cleanPrice: roundedCleanPrice.toFixed(4),
          dirtyPrice: normalizedDirtyPrice,
          settlementAmount: calculateSettlementAmountFromDirty(form.faceValue, normalizedDirtyPrice)
        };
      });
    } else {
      setForm(form => ({ ...form, [name]: value }));
    }
  }, [form, isinList, setError, setShowSellModal, isEditMode, editingTransactionId, originalEditValues, isinRequiresReselection, calculateSettlementAmountFromDirty]);

  // Monitor form reset specifically
  useEffect(() => {
    if (formKey > 0) {
      console.log('=== FORM RESET DETECTED ===');
      console.log('New Form Key:', formKey);
      console.log('Form after reset:', form);
      console.log('========================');
    }
  }, [formKey, form]);

  // Function to completely reset the form and all related state
  const resetFormCompletely = () => {
    console.log('=== RESETTING FORM COMPLETELY ===');
    setIsViewMode(false); // Clear view mode when resetting form
    setEditingTransactionId(null); // Clear editing transaction ID
    setEditingTransaction(null); // Clear stored transaction
    setIsEditMode(false); // Clear edit mode flag
    setOriginalEditValues({ faceValue: null, yield: null }); // Clear original edit values
    setIsinRequiresReselection(false); // Clear ISIN reselection flag
    console.log('Previous Form Key:', formKey);

    // Clear all form-related state first
    setSellDeals([]);
    setShowSellModal(false);
    setPortfolioFromSellModal(false);
    setError('');
    setSuccess('');
    setRetryCount(0);
    setIsSubmitting(false);

    // Reset form with a delay to ensure useEffect hooks don't interfere
    setTimeout(() => {
      setForm(getInitialFormState());
      setFormKey(prev => prev + 1); // Force form re-render
      console.log('Form reset completed');
      console.log('========================');
    }, 100);
  };

  // Function to retry failed requests
  const retryRequest = () => {
    if (retryCount < 2) { // Allow up to 2 retries
      console.log(`=== RETRYING REQUEST ===`);
      console.log(`Retry attempt ${retryCount + 1}/2`);
      setRetryCount(prev => prev + 1);
      setError('');

      // Simply trigger form submission again
      const form = document.querySelector('form.transaction-form');
      if (form) {
        form.requestSubmit();
      }
      console.log('========================');
    } else {
      setError('Maximum retry attempts reached. Please try again later.');
    }
  };

  // Fetch recent GSec transactions (optimized with useCallback)
  const fetchRecentGsecTransactions = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/isin-master/gsec/recent`);
      console.log('Recent GSec transactions:', response.data);

      // Debug: Log dirty price data from recent transactions
      console.log('=== RECENT TRANSACTIONS DIRTY PRICE DEBUG ===');
      if (response.data.data && response.data.data.length > 0) {
        response.data.data.forEach((tx, index) => {
          console.log(`Transaction ${index + 1}:`, {
            id: tx.id,
            isin: tx.isin,
            dirty_price: tx.dirty_price,
            clean_price: tx.clean_price,
            accrued_interest: tx.accrued_interest,
            face_value: tx.face_value
          });
        });
      }
      console.log('==========================================');

      setRecentGsecTransactions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching recent GSec transactions:', error);
      setError('Failed to load recent GSec transactions');
      setRecentGsecTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const rejectedGsecTransactions = useMemo(
    () => (recentGsecTransactions || []).filter((tx) => tx.status === 'rejected'),
    [recentGsecTransactions]
  );

  const filteredRecentGsecTransactions = useMemo(() => {
    const byDate = filterDealsByFilterDate(recentGsecTransactions, recentDealsFilterDate);
    const seen = new Set(byDate.map((tx) => tx.id));
    const extras = rejectedGsecTransactions.filter((tx) => !seen.has(tx.id));
    return [...extras, ...byDate];
  }, [recentGsecTransactions, recentDealsFilterDate, rejectedGsecTransactions]);

  // Form submission handlers

  // Handle form submission with industrial-level state management
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent event bubbling
    
    setError('');
    setSuccess('');

    // Prevent submission if in view mode (to prevent duplicate saves)
    if (isViewMode) {
      setError('Form is in view mode. Please clear the form or reset it before creating a new transaction.');
      return;
    }
    
    // When in edit mode, only allow submission if user explicitly clicked submit button
    // This prevents auto-submission when form is populated
    if (isEditMode && editingTransactionId) {
      // Check if this was triggered by the submit button
      const submitter = e.nativeEvent?.submitter;
      const isButtonClick = submitter && submitter.type === 'submit';
      
      if (!isButtonClick) {
        // This is likely an auto-submit (Enter key or other event)
        // Block it and show message to user
        console.log('=== AUTO-SUBMIT BLOCKED IN EDIT MODE ===');
        setError('Please click the "Submit" button to save your changes.');
        return;
      }
      
      // User explicitly clicked submit - clear edit mode flag
      setIsEditMode(false);
    }

    // Debug: Log form state before validation (optimized)
    console.log('=== FORM SUBMISSION ===');
    console.log('ISIN:', form.isin, 'Type:', form.transactionType, 'Amount:', form.faceValue);
    console.log('Loading:', isLoading, 'Submitting:', isSubmitting);
    console.log('========================');

    // Prevent multiple submissions with enhanced guard
    if (isLoading || isSubmitting) {
      console.log('=== SUBMISSION BLOCKED ===');
      return;
    }

    // Check if form is in a clean state (after reset) - but allow if we're editing a rejected deal
    const isFormClean = Object.values(form).every(value => value === '' || value === null || value === undefined);
    if (isFormClean && formKey > 0 && !editingTransactionId) {
      console.log('=== SUBMISSION BLOCKED - FORM IS CLEAN (WAITING FOR USER INPUT) ===');
      setError('Please fill in the form before submitting.');
      return;
    }

    // Removed problematic form reset check that was blocking subsequent submissions

    // Validate essential fields
    const requiredFields = [
      { name: 'ISIN', value: form.isin },
      { name: 'Transaction Type', value: form.transactionType },
      { name: 'Face Value', value: form.faceValue },
      { name: 'Value Date', value: form.valueDate }
    ];

    const missingFields = requiredFields.filter(field => !field.value);
    if (missingFields.length > 0) {
      setError(`Please fill in the following required fields: ${missingFields.map(f => f.name).join(', ')}`);
      return;
    }
    
    // If editing and ISIN requires reselection (because face value or yield changed), block submission
    if (isEditMode && editingTransactionId && isinRequiresReselection) {
      setError('Face Value or Yield has changed. Please reselect the ISIN to recalculate prices before submitting.');
      return;
    }

    // Check if we're editing a rejected deal
    if (editingTransactionId) {
      // Update existing rejected deal
      setIsLoading(true);
      setIsSubmitting(true);
      
      try {
        // IMPORTANT: Preserve the original deal_number to ensure we update the same deal, not create a new one
        const originalDealNumber = form.dealNumber || editingTransaction?.dealNumber || editingTransaction?.deal_number;
        
        if (!originalDealNumber) {
          setError('Cannot update: Deal number is missing. Please contact support.');
          setIsLoading(false);
          setIsSubmitting(false);
          return;
        }
        
        const updateData = {
          ...form,
          dealNumber: originalDealNumber, // Explicitly preserve deal number to prevent new deal creation
          userId: user.id,
          current_approval_level: 'front_office', // Resubmit goes to first tier (Front Office Verifier)
          status: 'pending',
          // Ensure dirty price is properly formatted (rounded to 4 decimals)
          dirtyPrice: form.dirtyPrice ? Math.round(parseFloat(form.dirtyPrice) * 10000) / 10000 : form.dirtyPrice,
          // Ensure clean price is properly formatted (rounded to 4 decimals)
          cleanPrice: form.cleanPrice ? Math.round(parseFloat(form.cleanPrice) * 10000) / 10000 : form.cleanPrice,
          // Ensure accrued interest is properly formatted (rounded to 4 decimals)
          accruedInterest: form.accruedInterest ? Math.round(parseFloat(form.accruedInterest) * 10000) / 10000 : form.accruedInterest,
        };

        console.log('=== UPDATING EXISTING DEAL ===');
        console.log('Transaction ID:', editingTransactionId);
        console.log('Deal Number:', originalDealNumber);
        console.log('Update Data:', updateData);
        console.log('=============================');

        const response = await axios.put(`${API_BASE_URL}/isin-master/gsec/${editingTransactionId}`, updateData);
        console.log('Rejected deal updated and resubmitted:', response.data);
        
        // Verify that the update was successful and didn't create a new deal
        if (response.data && response.data.success === false) {
          throw new Error(response.data.error || 'Failed to update deal');
        }

        setSuccess(`Deal ${originalDealNumber} updated and resubmitted for approval successfully!`);
        setEditingTransactionId(null);
        setEditingTransaction(null); // Clear stored transaction
        setIsEditMode(false); // Clear edit mode after successful submission
        setOriginalEditValues({ faceValue: null, yield: null }); // Clear original edit values
        setIsinRequiresReselection(false); // Clear ISIN reselection flag
        fetchRecentGsecTransactions();

        // IMPORTANT: keep isLoading/isSubmitting true (Submit button disabled) until the
        // form is actually cleared below. Clearing them immediately re-enables Submit
        // while editingTransactionId is already null but the form still holds the just-
        // submitted deal's data - a second click in that window would fall through to
        // the create/POST branch and insert a duplicate deal instead of being blocked.
        setTimeout(() => {
          resetFormCompletely();
          setIsLoading(false);
          setIsSubmitting(false);
        }, 1500);
        return;
      } catch (error) {
        console.error('Error updating rejected deal:', error);
        setError('Failed to update deal: ' + (error.response?.data?.message || error.message));
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }
    }

    // Add the user ID and approval level to the form data
    // If this is a Sell transaction, include sellDeals in the payload
    const formDataWithUser = {
      ...form,
      userId: user.id,
      // current_approval_level set by backend to front_office for new deals (3-tier)
      status: 'pending',
      // Ensure dirty price is properly formatted (truncated to 4 decimals)
      dirtyPrice: form.dirtyPrice ? Math.floor(parseFloat(form.dirtyPrice) * 10000) / 10000 : form.dirtyPrice,
      // Ensure clean price is properly formatted (rounded to 4 decimals)
      cleanPrice: form.cleanPrice ? Math.round(parseFloat(form.cleanPrice) * 10000) / 10000 : form.cleanPrice,
      // Ensure accrued interest is properly formatted (rounded to 4 decimals)
      accruedInterest: form.accruedInterest ? Math.round(parseFloat(form.accruedInterest) * 10000) / 10000 : form.accruedInterest,
      ...(form.transactionType === 'Sell' && sellDeals.length > 0 ? { sell_deals: sellDeals } : {})
    };

    // Debug: Log the dirty price being sent
    console.log('=== DIRTY PRICE DEBUG ===');
    console.log('Form dirtyPrice:', form.dirtyPrice);
    console.log('Form cleanPrice:', form.cleanPrice);
    console.log('Form accruedInterest:', form.accruedInterest);
    console.log('Formatted dirtyPrice (to backend):', formDataWithUser.dirtyPrice);
    console.log('Formatted cleanPrice (to backend):', formDataWithUser.cleanPrice);
    console.log('Formatted accruedInterest (to backend):', formDataWithUser.accruedInterest);

    // Debug: Log form state for second submission
    console.log('=== FORM STATE DEBUG ===');
    console.log('Form Key:', formKey);
    console.log('Is Form Clean:', isFormClean);
    console.log('Form Data:', form);
    console.log('========================');

    // Validate dirty price calculation
    const expectedDirtyPrice = parseFloat(form.cleanPrice || 0) + parseFloat(form.accruedInterestPer100 || 0);
    const actualDirtyPrice = parseFloat(form.dirtyPrice || 0);
    const priceDifference = Math.abs(expectedDirtyPrice - actualDirtyPrice);

    console.log('Expected dirty price:', expectedDirtyPrice);
    console.log('Actual dirty price:', actualDirtyPrice);
    console.log('Price difference:', priceDifference);
    console.log('Clean price:', form.cleanPrice);
    console.log('Accrued interest per 100:', form.accruedInterestPer100);

    if (priceDifference > 0.0001) {
      console.warn('⚠️ DIRTY PRICE MISMATCH DETECTED!');
      console.warn('This may cause calculation inconsistencies.');
    }
    console.log('========================');

    // Reset retry count for new submissions
    if (retryCount > 0) {
      setRetryCount(0);
    }

    // Set loading and submitting states
    setIsLoading(true);
    setIsSubmitting(true);

    // Single timeout mechanism (15 seconds) - optimized for backend timeout
    const requestTimeout = setTimeout(() => {
      console.log('=== REQUEST TIMEOUT ===');
      setIsLoading(false);
      setIsSubmitting(false);
      setError('Request timed out. The server is taking too long to respond. Please try again.');
    }, 60000); // Increased to 60s

    // Configure axios with optimized settings
    const axiosConfig = {
      timeout: 45000, // 45 second timeout (less than frontend timeout)
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    };

    console.log('=== SENDING REQUEST ===');
    console.log('URL:', `${API_BASE_URL}/isin-master/gsec`);
    console.log('========================');

    axios.post(`${API_BASE_URL}/isin-master/gsec`, formDataWithUser, axiosConfig)
      .then(res => {
        // Clear the timeout
        clearTimeout(requestTimeout);

        console.log('Success response:', res);
        const transactionId = res.data?.data?.id || res.data?.id || form.dealNumber;
        setCreatedGsecTransactionId(transactionId);
        setShowGsecDocumentSection(true);
        setSuccess('Transaction saved and sent for verification! You can now upload documents below.');
        const limitWarning = res.data?.limitWarning || res.data?.data?.limitWarning;
        const cpLimitWarning = res.data?.counterpartyLimitWarning || res.data?.data?.counterpartyLimitWarning;
        // Both limits can breach on the same deal, so show whichever fired.
        const warnings = [];
        if (limitWarning) {
          warnings.push(`${limitWarning.message} This deal exceeds your dealer limit — Middle Office has been notified.`);
        }
        if (cpLimitWarning) {
          warnings.push(`${cpLimitWarning.message} Middle Office has been notified.`);
        }
        if (warnings.length) setWarning(warnings.join(' '));
        window.scrollTo(0, 0); // Scroll to top to show the success message

        // Clear loading state immediately after successful save
        setIsLoading(false);
        setIsSubmitting(false);

        // Refresh blotter immediately
        fetchRecentGsecTransactions();

        // Reset form after a short delay to show success message
        setTimeout(() => {
          resetFormCompletely();
        }, 1500);
      })
      .catch(err => {
        // Clear the timeout
        clearTimeout(requestTimeout);

        console.error('Error saving GSec entry:', err);
        console.log('Error details:', {
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data,
          message: err.message,
          code: err.code,
          config: {
            url: err.config?.url,
            method: err.config?.method,
            baseURL: err.config?.baseURL,
            timeout: err.config?.timeout
          }
        });

        // Clear loading state on error
        setIsLoading(false);
        setIsSubmitting(false);

        // Handle different types of errors
        if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
          setError('Request timed out. The server is taking too long to respond. Please try again.');
        } else if (err.code === 'NETWORK_ERROR' || !err.response) {
          setError('Network error. Please check your internet connection and try again.');
        } else if (err.response?.data?.message === 'GSec limit exceeded') {
          const details = err.response?.data?.limitDetails || {};
          setError(`Limit exceeded: ${err.response?.data?.details}. Current exposure: ${details.currentExposure}, Limit: ${details.limit}`);
        } else if (err.response?.data?.error && err.response?.data?.error.toLowerCase().includes('sell')) {
          // Backend error related to sell deals (e.g., overselling, invalid sell)
          setError(`Sell transaction error: ${err.response.data.error}`);
        } else if (err.response?.status === 500) {
          setError('Server error. Please try again in a few moments. If the problem persists, contact support.');
        } else if (err.response?.status === 400) {
          setError(`Invalid request: ${err.response?.data?.error || err.response?.data?.message || 'Please check your input and try again.'}`);
        } else if (err.response) {
          // Server responded with non-2xx status
          setError(`Save failed (${err.response.status}): ${err.response?.data?.error || err.response?.statusText || 'Unknown server error'}`);
        } else {
          // Other errors
          setError('Save failed: ' + (err.message || 'Unknown error'));
        }
      });
  }, [form, isLoading, isSubmitting, isViewMode, isEditMode, editingTransactionId, retryCount, resetFormCompletely, user, fetchRecentGsecTransactions]);


  // Handle GSec transaction authorization - single approval level
  const handleAuthorize = async (status) => {
    try {
      if (status === 'rejected' && !comment) {
        setError('Comment is required when rejecting a GSec transaction');
        return;
      }
      setIsLoading(true);
      // Get user from localStorage for auth header
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      let newStatus = status;
      
      // 3-tier: backend advances level (front_office -> back_office_verifier -> back_office_final -> final_approved)
      if (status === 'approved') {
        newStatus = 'approved';
      } else if (status === 'rejected') {
        newStatus = 'rejected';
      }
      
      await axios.put(`${API_BASE_URL}/isin-master/gsec/${selectedTransaction.id}/status`, {
        status: newStatus,
        comment: status === 'rejected' ? comment : '',
        userId: user.id
      }, {
        headers: {
          'x-user-data': JSON.stringify(user)
        }
      });
      setSuccess(`GSec transaction ${status} successfully`);
      // Refresh the transactions list
      fetchRecentGsecTransactions();
      // Clear the selected transaction and comment
      setSelectedTransaction(null);
      setComment('');
      setShowDetails(false);
    } catch (err) {
      console.error('Authorization error:', err);
      setError(err.response?.data?.message || 'Failed to update GSec transaction status');
    } finally {
      setIsLoading(false);
    }
  };


  const handleApprove = async () => {
    await handleAuthorize('approved');
  };

  // eslint-disable-next-line no-unused-vars
  const handleReject = async () => {
    await handleAuthorize('rejected');
  };

  const handleViewDetails = (transaction) => {
    // Set view mode to prevent accidental duplicate saves
    setIsViewMode(true);
    
    // Always fill all fields, using both snake_case and camelCase keys and formatting dates
    // IMPORTANT: Clear dealNumber to prevent duplicate saves if form is accidentally submitted
    setForm(form => ({
      ...form,
      tradeDate: transaction.tradeDate ? formatLocalYMD(transaction.tradeDate) : (transaction.trade_date ? formatLocalYMD(transaction.trade_date) : ''),
      tradeType: transaction.tradeType || transaction.trade_type || '',
      transactionType: transaction.transactionType || transaction.transaction_type || '',
      counterparty: transaction.counterparty || transaction.counterparty_id || '',
      dealNumber: '', // Clear dealNumber to prevent duplicate saves
      isin: transaction.isin || '',
      faceValue: transaction.faceValue || transaction.face_value || '',
      valueDate: transaction.valueDate ? formatLocalYMD(transaction.valueDate) : (transaction.value_date ? formatLocalYMD(transaction.value_date) : ''),
      nextCouponDate: transaction.nextCouponDate ? formatLocalYMD(transaction.nextCouponDate) : (transaction.next_coupon_date ? formatLocalYMD(transaction.next_coupon_date) : ''),
      lastCouponDate: transaction.lastCouponDate ? formatLocalYMD(transaction.lastCouponDate) : (transaction.last_coupon_date ? formatLocalYMD(transaction.last_coupon_date) : ''),
      numberOfDaysInterestAccrued: transaction.numberOfDaysInterestAccrued || transaction.number_of_days_interest_accrued || '',
      numberOfDaysForCouponPeriod: transaction.numberOfDaysForCouponPeriod || transaction.number_of_days_for_coupon_period || '',
      accruedInterest: transaction.accruedInterest || transaction.accrued_interest || '',
      accruedInterestBase: transaction.accruedInterestBase || transaction.accrued_interest_base || '',
      couponInterest: transaction.couponInterest || transaction.coupon_interest || '',
      cleanPrice: (transaction.cleanPrice != null && transaction.cleanPrice !== '') ? Number(transaction.cleanPrice).toFixed(4) : (transaction.clean_price != null && transaction.clean_price !== '' ? Number(transaction.clean_price).toFixed(4) : ''),
      dirtyPrice: (transaction.dirtyPrice != null && transaction.dirtyPrice !== '') ? Number(transaction.dirtyPrice).toFixed(4) : (transaction.dirty_price != null && transaction.dirty_price !== '' ? Number(transaction.dirty_price).toFixed(4) : ''),
      settlementAmount: (transaction.settlementAmount != null && transaction.settlementAmount !== '') ? Number(transaction.settlementAmount).toFixed(4) : (transaction.settlement_amount != null && transaction.settlement_amount !== '' ? Number(transaction.settlement_amount).toFixed(4) : ''),
      couponDate1: transaction.couponDate1 || transaction.coupon_date_1 || '',
      couponDate2: transaction.couponDate2 || transaction.coupon_date_2 || '',
      issueDate: transaction.issueDate ? formatLocalYMD(transaction.issueDate) : (transaction.issue_date ? formatLocalYMD(transaction.issue_date) : ''),
      maturityDate: transaction.maturityDate ? formatLocalYMD(transaction.maturityDate) : (transaction.maturity_date ? formatLocalYMD(transaction.maturity_date) : ''),
      couponRate: transaction.couponRate || transaction.coupon_rate || '',
      yieldRate: transaction.yieldRate || transaction.yield_rate || '',
      cleanPriceBase: transaction.cleanPriceBase || transaction.clean_price_base || '',
      accruedInterestCalculation: transaction.accruedInterestCalculation || transaction.accrued_interest_calculation || '',
      broker: transaction.broker || '',
      accruedInterestSixDecimals: transaction.accruedInterestSixDecimals || transaction.accrued_interest_six_decimals || '',
      accruedInterestFor100: transaction.accruedInterestFor100 || transaction.accrued_interest_for_100 || '',
      settlementMode: transaction.settlementMode || transaction.settlement_mode || '',
      fundMovement: String(transaction.fundMovement || transaction.fund_movement || 'no').toLowerCase() === 'yes' ? 'yes' : 'no',
      couponDates: transaction.couponDates || transaction.coupon_dates || '',
      yield: transaction.yield || '',
      brokerage: transaction.brokerage || '',
      currency: transaction.currency || 'LKR',
      portfolio: transaction.portfolio || '',
      strategy: transaction.strategy || '',
      accruedInterestAdjustment: transaction.accruedInterestAdjustment || transaction.accrued_interest_adjustment || '',
      cleanPriceAdjustment: transaction.cleanPriceAdjustment || transaction.clean_price_adjustment || '',
      custodian: transaction.custodian || 'A'
    }));
    setError && setError('');
    setSuccess && setSuccess('');
    
    // Fetch bank details if settlement mode exists to display bank info in view mode
    const settlementModeValue = transaction.settlementMode || transaction.settlement_mode || '';
    if (settlementModeValue) {
      fetchBankDetailsByCode(settlementModeValue)
        .then(details => {
          if (details) {
            setSelectedBankDetails({
              bank_name: details.bank_name || '',
              bank_account_number: details.bank_account_number || '',
              bank_branch: details.bank_branch || ''
            });
          } else {
            setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
          }
        })
        .catch(() => setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' }));
    } else {
      setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
    }
    
    // Show a message that this is view-only mode
    setSuccess && setSuccess('Viewing transaction details. Form is in view mode. Clear form to create a new transaction.');
  };

  // Handle edit GSec transaction (for edit modal)
  const handleEdit = (transaction) => {
    setEditForm({
      ...transaction,
      // Use local date parser for edit modal
      custodian: transaction.custodian || '',
      tradeDate: transaction.trade_date ? formatLocalYMD(parseLocalYMD(transaction.trade_date)) : '',
      valueDate: transaction.value_date ? formatLocalYMD(parseLocalYMD(transaction.value_date)) : ''
    });
    setShowEditModal(true);
  };

  // Handle edit rejected deal - populate main form for editing and resubmission
  const handleEditRejectedDeal = (transaction) => {
    // Clear view mode to allow editing
    setIsViewMode(false);
    
    // Set edit mode flag to prevent auto-submission
    setIsEditMode(true);
    
    // Prevent any auto-submission - user must explicitly click submit
    setIsSubmitting(false);
    setIsLoading(false);
    
    // Store the full transaction object for reference during update
    setEditingTransaction(transaction);
    
    // Store original face value and yield for validation
    const originalFaceValue = transaction.faceValue || transaction.face_value || '';
    const originalYield = transaction.yield || '';
    setOriginalEditValues({ 
      faceValue: originalFaceValue, 
      yield: originalYield 
    });
    setIsinRequiresReselection(false); // Reset flag

    // Restore accruedInterestPer100 (dirtyPrice - cleanPrice), the per-100 basis used by the
    // Clean Price Adjustment / Accrued Interest Adjustment handlers. This value is never
    // persisted to the DB, so it must be rederived here - otherwise re-editing an adjustment
    // recalculates against a base of 0 and wipes out the previously-saved accrued interest.
    const restoredCleanPrice = Number(transaction.cleanPrice ?? transaction.clean_price) || 0;
    const restoredDirtyPrice = Number(transaction.dirtyPrice ?? transaction.dirty_price) || 0;
    const restoredAccruedInterestPer100 = (restoredDirtyPrice - restoredCleanPrice).toFixed(4);

    // Adjustment columns are DECIMAL(20,6) in the DB and come back as long decimal strings
    // (e.g. "0.000000"); normalize so the inputs show a plain number instead of extra zeros.
    const normalizeAdjustment = (val) => {
      const num = parseFloat(val);
      return num ? String(num) : '';
    };

    // Populate the main form with transaction data (similar to handleViewDetails but for editing)
    setForm(form => ({
      ...form,
      tradeDate: transaction.tradeDate ? formatLocalYMD(transaction.tradeDate) : (transaction.trade_date ? formatLocalYMD(transaction.trade_date) : ''),
      tradeType: transaction.tradeType || transaction.trade_type || '',
      transactionType: transaction.transactionType || transaction.transaction_type || '',
      counterparty: transaction.counterparty || transaction.counterparty_id || '',
      dealNumber: transaction.dealNumber || transaction.deal_number || '', // Keep deal number for update
      isin: transaction.isin || '',
      faceValue: transaction.faceValue || transaction.face_value || '',
      valueDate: transaction.valueDate ? formatLocalYMD(transaction.valueDate) : (transaction.value_date ? formatLocalYMD(transaction.value_date) : ''),
      nextCouponDate: transaction.nextCouponDate ? formatLocalYMD(transaction.nextCouponDate) : (transaction.next_coupon_date ? formatLocalYMD(transaction.next_coupon_date) : ''),
      lastCouponDate: transaction.lastCouponDate ? formatLocalYMD(transaction.lastCouponDate) : (transaction.last_coupon_date ? formatLocalYMD(transaction.last_coupon_date) : ''),
      numberOfDaysInterestAccrued: transaction.numberOfDaysInterestAccrued || transaction.number_of_days_interest_accrued || '',
      numberOfDaysForCouponPeriod: transaction.numberOfDaysForCouponPeriod || transaction.number_of_days_for_coupon_period || '',
      accruedInterest: transaction.accruedInterest || transaction.accrued_interest || '',
      accruedInterestBase: transaction.accruedInterestBase || transaction.accrued_interest_base || '',
      couponInterest: transaction.couponInterest || transaction.coupon_interest || '',
      cleanPrice: (transaction.cleanPrice != null && transaction.cleanPrice !== '') ? Number(transaction.cleanPrice).toFixed(4) : (transaction.clean_price != null && transaction.clean_price !== '' ? Number(transaction.clean_price).toFixed(4) : ''),
      dirtyPrice: (transaction.dirtyPrice != null && transaction.dirtyPrice !== '') ? Number(transaction.dirtyPrice).toFixed(4) : (transaction.dirty_price != null && transaction.dirty_price !== '' ? Number(transaction.dirty_price).toFixed(4) : ''),
      settlementAmount: (transaction.settlementAmount != null && transaction.settlementAmount !== '') ? Number(transaction.settlementAmount).toFixed(4) : (transaction.settlement_amount != null && transaction.settlement_amount !== '' ? Number(transaction.settlement_amount).toFixed(4) : ''),
      couponDate1: transaction.couponDate1 || transaction.coupon_date_1 || '',
      couponDate2: transaction.couponDate2 || transaction.coupon_date_2 || '',
      issueDate: transaction.issueDate ? formatLocalYMD(transaction.issueDate) : (transaction.issue_date ? formatLocalYMD(transaction.issue_date) : ''),
      maturityDate: transaction.maturityDate ? formatLocalYMD(transaction.maturityDate) : (transaction.maturity_date ? formatLocalYMD(transaction.maturity_date) : ''),
      couponRate: transaction.couponRate || transaction.coupon_rate || '',
      yieldRate: transaction.yieldRate || transaction.yield_rate || '',
      cleanPriceBase: transaction.cleanPriceBase || transaction.clean_price_base || '',
      accruedInterestCalculation: transaction.accruedInterestCalculation || transaction.accrued_interest_calculation || '',
      broker: transaction.broker || '',
      accruedInterestSixDecimals: transaction.accruedInterestSixDecimals || transaction.accrued_interest_six_decimals || '',
      accruedInterestFor100: transaction.accruedInterestFor100 || transaction.accrued_interest_for_100 || '',
      settlementMode: transaction.settlementMode || transaction.settlement_mode || '',
      fundMovement: String(transaction.fundMovement || transaction.fund_movement || 'no').toLowerCase() === 'yes' ? 'yes' : 'no',
      couponDates: transaction.couponDates || transaction.coupon_dates || '',
      yield: transaction.yield || '',
      brokerage: transaction.brokerage || '',
      currency: transaction.currency || 'LKR',
      portfolio: transaction.portfolio || '',
      strategy: transaction.strategy || '',
      accruedInterestAdjustment: normalizeAdjustment(transaction.accruedInterestAdjustment ?? transaction.accrued_interest_adjustment),
      cleanPriceAdjustment: normalizeAdjustment(transaction.cleanPriceAdjustment ?? transaction.clean_price_adjustment),
      accruedInterestPer100: restoredAccruedInterestPer100,
      custodian: transaction.custodian || 'A'
    }));

    // Store the transaction ID for updating
    setEditingTransactionId(transaction.id);
    
    // Fetch bank details if settlement mode exists
    const settlementModeValue = transaction.settlementMode || transaction.settlement_mode || '';
    if (settlementModeValue) {
      fetchBankDetailsByCode(settlementModeValue)
        .then(details => {
          if (details) {
            setSelectedBankDetails({
              bank_name: details.bank_name || '',
              bank_account_number: details.bank_account_number || '',
              bank_branch: details.bank_branch || ''
            });
          } else {
            setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
          }
        })
        .catch(() => {
          setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
        });
    } else {
      setSelectedBankDetails({ bank_name: '', bank_account_number: '', bank_branch: '' });
    }
    
    setError && setError('');
    setSuccess && setSuccess('Editing rejected deal. Review and modify the fields below, then click "Submit" to resubmit for approval.');
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Force form to not be in a "clean" state to prevent auto-blocking
    // This ensures the form is ready for editing
    setFormKey(prev => prev + 1);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      setEditLoading(true);
      setEditError('');

      // Get user from localStorage for auth tracking
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      // Add the user ID to the edit form data
      const editFormWithUser = {
        ...editForm,
        userId: user.id,
        custodian: form.custodian
      };

      // Ensure financial calculations follow requirements
      // Accrued Interest and Clean Price are both rounded to 4 decimal places
      if (editFormWithUser.accruedInterest) {
        const accruedInterest = Math.round(parseFloat(editFormWithUser.accruedInterest) * 10000) / 10000;
        editFormWithUser.accruedInterest = accruedInterest.toFixed(4);
      }

      if (editFormWithUser.cleanPrice) {
        const cleanPrice = Math.round(parseFloat(editFormWithUser.cleanPrice) * 10000) / 10000;
        editFormWithUser.cleanPrice = cleanPrice.toFixed(4);
      }

      // Calculate dirty price as clean price + accrued interest
      if (editFormWithUser.cleanPrice && editFormWithUser.accruedInterest) {
        const dirtyPrice = parseFloat(editFormWithUser.cleanPrice) + parseFloat(editFormWithUser.accruedInterest);
        editFormWithUser.dirtyPrice = dirtyPrice.toFixed(4);
      }

      const response = await axios.put(`${API_BASE_URL}/isin-master/gsec/${editForm.id}`, editFormWithUser);
      console.log('GSec transaction updated:', response.data);

      setSuccess('GSec transaction updated successfully');
      setShowEditModal(false);
      fetchRecentGsecTransactions();
    } catch (error) {
      console.error('Error updating GSec transaction:', error);
      setEditError('Failed to update GSec transaction: ' + (error.response?.data?.message || error.message));
    } finally {
      setEditLoading(false);
    }
  };

  // Add useEffect to fetch coupon months when ISIN changes
  const [selectedIsinForCouponMonths, setSelectedIsinForCouponMonths] = useState('');
  useEffect(() => {
    if (!selectedIsinForCouponMonths) {
      setForm(form => ({ ...form, couponDates: '' }));
      return;
    }
    const fetchCouponDatesForIsin = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/isin-master/${selectedIsinForCouponMonths}/coupon-months`);
        const months = res.data.data || [];
        setForm(form => ({ ...form, couponDates: months.join(', ') }));
      } catch (err) {
        setForm(form => ({ ...form, couponDates: '' }));
      }
    };
    fetchCouponDatesForIsin();
  }, [selectedIsinForCouponMonths]);

  // Add a debug log before the return in the component
  console.log('[DEBUG] Form state at render:', form);

  // Handler for portfolio selection in sell modal
  const handleSellModalPortfolioChange = (portfolio) => {
    if (portfolio) {
      setPortfolioFromSellModal(true);
      setForm(f => ({
        ...f,
        portfolio: portfolio
      }));
    }
  };

  // Handler for confirming deals from the sell modal
  const handleSellDealsConfirm = ({ deals, portfolio }) => {
    setSellDeals(deals);
    setShowSellModal(false);

    if (portfolio) {
      setPortfolioFromSellModal(true);
    }

    // Do not override the sell face value from allocations. The modal only
    // confirms when allocated amounts already match the form face value.
    setForm(f => ({
      ...f,
      portfolio: portfolio || f.portfolio
    }));
  };

  return (
    <>
      <GsecSellDealModal
        show={showSellModal}
        onClose={() => setShowSellModal(false)}
        onConfirm={handleSellDealsConfirm}
        onPortfolioChange={handleSellModalPortfolioChange}
        isin={form.isin}
        gsecFaceValue={form.faceValue}
        cleanPrice={form.cleanPrice}
        isinData={isinList.find(isin => isin.isin_number === form.isin)}
        formValueDate={form.valueDate}
      />
      <div>
        <div className="transaction-page">
          <div className="main-content" style={{ marginLeft: 1, padding: '2rem 4rem' }}>

            {/* Error message */}
            {error && (
              <div style={{ background: '#f8d7da', color: '#721c24', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid #f5c6cb' }}>
                {error}
                {retryCount > 0 && retryCount < 2 && (error.includes('timeout') || error.includes('Network error') || error.includes('Server error')) && (
                  <div style={{ marginTop: '10px' }}>
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={retryRequest}
                      disabled={isLoading}
                      style={{ marginRight: '10px' }}
                    >
                      {isLoading ? 'Retrying...' : `Retry (${retryCount}/2)`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Warning message for coupon dates/accrued interest */}
            {warning && (
              <div style={{ background: '#fff3cd', color: '#856404', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid #ffeeba' }}>
                {warning}
              </div>
            )}

            {/* Success message */}
            {success && (
              <div style={{ background: '#d4edda', color: '#155724', padding: '10px', marginBottom: '15px', borderRadius: '4px', border: '1px solid #c3e6cb' }}>
                {success}
              </div>
            )}

            {/* Multi-Level Authorization Panels (3-tier) */}
            <div className="multi-auth-panels">
              {/* Front Office Verifier Panel */}
              {userRole === 'front_office' && (
                <div className="auth-panel front-office-panel">
                  <h3>Front Office Verifier</h3>
                  {recentGsecTransactions.filter(tx => tx.status === 'pending' && (tx.current_approval_level || 'front_office') === 'front_office').length === 0 ? (
                    <div>No transactions pending front office verification.</div>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Deal #</th>
                          <th>Type</th>
                          <th>ISIN</th>
                          <th>Counterparty</th>
                          <th>Face Value</th>
                          <th>Dirty Price</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentGsecTransactions.filter(tx => tx.status === 'pending' && (tx.current_approval_level || 'front_office') === 'front_office').map(tx => (
                          <tr key={tx.id}>
                            <td>{formatLocalYMD(parseLocalYMD(tx.trade_date))}</td>
                            <td>{tx.deal_number}</td>
                            <td>{tx.transaction_type}</td>
                            <td>{tx.isin}</td>
                            <td>{counterparties.find(cp => cp.id === tx.counterparty || String(cp.unique_id) === String(tx.counterparty_id || tx.counterparty))?.name || tx.counterparty_name || tx.counterparty_id || tx.counterparty}</td>
                            <td>{tx.face_value ? Number(tx.face_value).toFixed(4) : ''}</td>
                            <td>{tx.dirty_price ? Number(tx.dirty_price).toFixed(4) : ''}</td>
                            <td>{tx.status}</td>
                            <td>
                              <button className="btn btn-success btn-sm mr-2" onClick={() => handleViewDetails(tx)}>View / Authorize</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {/* Back Office Verifier Panel */}
              {userRole === 'back_office_verifier' && (
                <div className="auth-panel back-office-verifier-panel">
                  <h3>Back Office Verifier</h3>
                  {recentGsecTransactions.filter(tx => tx.status === 'pending' && tx.current_approval_level === 'back_office_verifier').length === 0 ? (
                    <div>No transactions pending back office verification.</div>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Deal #</th>
                          <th>Type</th>
                          <th>ISIN</th>
                          <th>Counterparty</th>
                          <th>Face Value</th>
                          <th>Dirty Price</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentGsecTransactions.filter(tx => tx.status === 'pending' && tx.current_approval_level === 'back_office_verifier').map(tx => (
                          <tr key={tx.id}>
                            <td>{formatLocalYMD(parseLocalYMD(tx.trade_date))}</td>
                            <td>{tx.deal_number}</td>
                            <td>{tx.transaction_type}</td>
                            <td>{tx.isin}</td>
                            <td>{counterparties.find(cp => cp.id === tx.counterparty || String(cp.unique_id) === String(tx.counterparty_id || tx.counterparty))?.name || tx.counterparty_name || tx.counterparty_id || tx.counterparty}</td>
                            <td>{tx.face_value ? Number(tx.face_value).toFixed(4) : ''}</td>
                            <td>{tx.dirty_price ? Number(tx.dirty_price).toFixed(4) : ''}</td>
                            <td>{tx.status}</td>
                            <td>
                              <button className="btn btn-success btn-sm mr-2" onClick={() => handleViewDetails(tx)}>View / Authorize</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {/* Back Office Final Authorizer Panel */}
              {userRole === 'back_office_final' && (
                <div className="auth-panel back-office-final-panel">
                  <h3>Back Office Final Authorization</h3>
                  {recentGsecTransactions.filter(tx => tx.status === 'pending' && tx.current_approval_level === 'back_office_final').length === 0 ? (
                    <div>No transactions pending final authorization.</div>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Deal #</th>
                          <th>Type</th>
                          <th>ISIN</th>
                          <th>Counterparty</th>
                          <th>Face Value</th>
                          <th>Dirty Price</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentGsecTransactions.filter(tx => tx.status === 'pending' && tx.current_approval_level === 'back_office_final').map(tx => (
                          <tr key={tx.id}>
                            <td>{formatLocalYMD(parseLocalYMD(tx.trade_date))}</td>
                            <td>{tx.deal_number}</td>
                            <td>{tx.transaction_type}</td>
                            <td>{tx.isin}</td>
                            <td>{counterparties.find(cp => cp.id === tx.counterparty || String(cp.unique_id) === String(tx.counterparty_id || tx.counterparty))?.name || tx.counterparty_name || tx.counterparty_id || tx.counterparty}</td>
                            <td>{tx.face_value ? Number(tx.face_value).toFixed(4) : ''}</td>
                            <td>{tx.dirty_price ? Number(tx.dirty_price).toFixed(4) : ''}</td>
                            <td>{tx.status}</td>
                            <td>
                              <button className="btn btn-success btn-sm mr-2" onClick={() => handleViewDetails(tx)}>View / Authorize</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>

            {GSEC_LETTER_ROLES.includes(userRole) && (
              <GsecFinalApprovedLettersPanel
                transactions={recentGsecTransactions}
                valueDateYmd={recentDealsFilterDate || null}
              />
            )}

            {/* Authorization Dialog (modal, reused for all panels) */}
            {showDetails && selectedTransaction && (
              <div className="authorization-dialog">
                <h3>GSec Transaction Details</h3>
                <div className="auth-details">
                  <div><strong>ISIN:</strong> {selectedTransaction.isin}</div>
                  <div><strong>Trade Date:</strong> {formatLocalYMD(parseLocalYMD(selectedTransaction.trade_date))}</div>
                  <div><strong>Value Date:</strong> {formatLocalYMD(parseLocalYMD(selectedTransaction.value_date))}</div>
                  <div><strong>Face Value:</strong> {selectedTransaction.face_value ? Number(selectedTransaction.face_value).toFixed(4) : ''}</div>
                  <div><strong>Dirty Price:</strong> {selectedTransaction.dirty_price ? Number(selectedTransaction.dirty_price).toFixed(4) : ''}</div>
                  <div><strong>Clean Price:</strong> {selectedTransaction.clean_price ? Number(selectedTransaction.clean_price).toFixed(4) : ''}</div>
                  <div><strong>Accrued Interest:</strong> {selectedTransaction.accrued_interest ? Number(selectedTransaction.accrued_interest).toFixed(4) : ''}</div>
                  <div><strong>Status:</strong> <span className={`status-badge ${selectedTransaction.status || 'pending'}`}>{selectedTransaction.status || 'Pending'}</span></div>
                </div>
                {(canAuthorize(selectedTransaction) && selectedTransaction.status === 'pending') && (
                  <div className="auth-actions">
                    <button className="btn btn-success" onClick={handleApprove}>Approve</button>
                    <div className="reject-section">
                      <textarea
                        placeholder="Enter reason for rejection"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        className="form-control"
                      ></textarea>
                      <button className="btn btn-danger" onClick={() => handleAuthorize('rejected')}>Reject</button>
                    </div>
                  </div>
                )}
                <button className="btn btn-secondary" onClick={() => setShowDetails(false)}>Close</button>
              </div>
            )}

            {/* Edit Modal */}
            {showEditModal && (
              <div className="edit-modal">
                <div className="modal-body">
                  <div className="edit-modal-content">
                    <h3>Edit GSec Transaction</h3>
                    <form onSubmit={handleEditSubmit}>
                      <div className="form-row">
                        <div className="form-group col-md-6">
                          <label>ISIN</label>
                          <input
                            type="text"
                            className="form-control"
                            name="isin"
                            value={editForm.isin || ''}
                            onChange={handleEditFormChange}
                            required
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Transaction Type</label>
                          <select
                            className="form-control"
                            name="transaction_type"
                            value={editForm.transaction_type || ''}
                            onChange={handleEditFormChange}
                            required
                          >
                            <option value="">Select Type</option>
                            <option value="Buy">Buy</option>
                            <option value="Sell">Sell</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row">
                        <div className="form-group col-md-6">
                          <label>Accrued Interest</label>
                          <input
                            type="number"
                            step="0.0001"
                            className="form-control"
                            name="accrued_interest"
                            value={editForm.accrued_interest || ''}
                            onChange={handleEditFormChange}
                            required
                          />
                        </div>
                        <div className="form-group col-md-6">
                          <label>Portfolio</label>
                          <input
                            type="text"
                            className="form-control"
                            name="portfolio"
                            value={editForm.portfolio || ''}
                            onChange={handleEditFormChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>Strategy</label>
                        <input
                          type="text"
                          className="form-control"
                          name="strategy"
                          value={editForm.strategy || ''}
                          onChange={handleEditFormChange}
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Comment (required for rejection)</label>
                        <textarea
                          className="form-control"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                          placeholder="Provide a reason if rejecting this transaction"
                          rows="3"
                        ></textarea>
                      </div>

                      <div className="d-flex justify-content-end">
                        <button
                          className="btn btn-success mr-2"
                          onClick={handleApprove}
                          disabled={isLoading}
                        >
                          {isLoading ? 'Processing...' : 'Approve'}
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={handleReject}
                          disabled={isLoading || !comment.trim()}
                        >
                          {isLoading ? 'Processing...' : 'Reject'}
                        </button>
                      </div>
                      <div className="form-group text-right">
                        <button type="submit" className="btn btn-primary" disabled={editLoading}>
                          {editLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}

            {/* Only show the transaction form for non-authorizer users */}
            {!isAuthorizer && (
              <form key={formKey} className="transaction-form" onSubmit={handleSubmit}>
                <div className="form-grid">
                </div>
                {/* --- GSec Form Fields in TransactionPage Grid Layout --- */}
                <div className="form-grid">
                  {/* Trade Date */}
                  <div className="form-group">
                    <label className="form-label">Trade Date</label>
                    <input 
                      type="date" 
                      name="tradeDate" 
                      value={form.tradeDate} 
                      onChange={handleChange} 
                      min={DATE_INPUT_MIN}
                      max={DATE_INPUT_MAX}
                      className={`form-control ${tradeDateHolidayError ? 'is-invalid' : ''}`}
                      required 
                    />
                    {tradeDateHolidayError && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                        {tradeDateHolidayError}
                      </div>
                    )}
                  </div>
                  {/* Value Date */}
                  <div className="form-group">
                    <label className="form-label">Value Date</label>
                    <input 
                      type="date" 
                      name="valueDate" 
                      value={form.valueDate} 
                      onChange={handleChange} 
                      min={DATE_INPUT_MIN}
                      max={DATE_INPUT_MAX}
                      className={`form-control ${valueDateHolidayError ? 'is-invalid' : ''}`}
                      required 
                    />
                    {valueDateHolidayError && (
                      <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                        {valueDateHolidayError}
                      </div>
                    )}
                  </div>
                  {/* Face Value */}
                  <div className="form-group">
                    <label className="form-label">Face Value</label>
                    <input
                      name="faceValue"
                      value={
                        form.faceValue && !isNaN(Number(form.faceValue))
                          ? Number(form.faceValue).toLocaleString(undefined)
                          : ''
                      }
                      onChange={e => {
                        const raw = e.target.value.replace(/,/g, '');
                        handleChange({
                          target: {
                            name: 'faceValue',
                            value: raw
                          }
                        });
                      }}
                      className="form-control"
                      inputMode="numeric"
                    />
                  </div>
                  {/* Yield */}
                  <div className="form-group">
                    <label className="form-label">Yield</label>
                    <input
                      name="yield"
                      value={form.yield}
                      onChange={handleChange}
                      className="form-control"
                      type="number"
                      step="0.000001"
                    />
                    {!form.yield && (
                      <div style={{ color: '#dc3545', marginTop: 4, fontSize: 13 }}>
                        Enter yield to calculate Dirty and Clean Price.
                      </div>
                    )}
                  </div>
                  {/* ISIN Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="isin">
                      ISIN
                      {isinRequiresReselection && (
                        <span style={{ color: '#dc3545', marginLeft: '8px', fontSize: '0.9em', fontWeight: 'bold' }}>
                          * Must Reselect
                        </span>
                      )}
                    </label>
                    {isinRequiresReselection && (
                      <div style={{ 
                        padding: '8px', 
                        marginBottom: '8px', 
                        background: '#fff3cd', 
                        border: '1px solid #ffc107', 
                        borderRadius: '4px',
                        color: '#856404',
                        fontSize: '0.9em'
                      }}>
                        ⚠️ Face Value or Yield has changed. Please reselect the ISIN to recalculate prices.
                      </div>
                    )}
                    <input
                      type="text"
                      className="form-control"
                      value={isinSearch}
                      onChange={(e) => setIsinSearch(e.target.value)}
                      placeholder="Search ISIN (e.g., last 2–4 chars)"
                      disabled={!form.faceValue || !form.yield}
                      style={{ marginBottom: 8 }}
                    />
                    <select
                      id="isin"
                      name="isin"
                      className="form-control"
                      value={form.isin}
                      onChange={handleChange}
                      required
                      disabled={!form.faceValue || !form.yield}
                      style={isinRequiresReselection ? { border: '2px solid #dc3545' } : {}}
                    >
                      <option value="">Select ISIN</option>
                      {memoIsins}
                    </select>
                    {(!form.faceValue || !form.yield) && (
                      <div style={{ color: '#dc3545', marginTop: 4, fontSize: 13 }}>
                        Please fill Face Value and Yield before selecting an ISIN.
                      </div>
                    )}
                  </div>

                  {/* Transaction Type Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="transactionType">Transaction Type</label>
                    <select
                      id="transactionType"
                      name="transactionType"
                      className="form-control"
                      value={form.transactionType}
                      onChange={handleChange}
                      required
                      style={{
                        ...getDerivedFieldStyle(form.transactionType),
                        fontWeight: form.transactionType ? 600 : 400,
                      }}
                    >
                      <option value="">Select Type</option>
                      <option value="Buy">Buy</option>
                      <option value="Sell">Sell</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fund Movement</label>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', minHeight: '40px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="radio"
                          name="fundMovement"
                          value="yes"
                          checked={form.fundMovement === 'yes'}
                          onChange={handleChange}
                        />
                        Yes
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input
                          type="radio"
                          name="fundMovement"
                          value="no"
                          checked={form.fundMovement !== 'yes'}
                          onChange={handleChange}
                        />
                        No
                      </label>
                    </div>
                  </div>
                  {/* Counterparty Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="counterparty">Counterparty</label>
                    <select
                      id="counterparty"
                      name="counterparty"
                      className="form-control"
                      value={form.counterparty}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Counterparty</option>
                      {memoCounterparties}
                    </select>
                  </div>
                  {/* Broker Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="broker">Broker</label>
                    <select
                      id="broker"
                      name="broker"
                      className="form-control"
                      value={form.broker}
                      onChange={handleChange}
                    >
                      <option value="">Select Broker</option>
                      {brokers.map(b => (
                        <option key={b.id} value={b.id}>{b.broker_name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Portfolio Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="portfolio">Portfolio</label>
                    <select
                      className="form-control"
                      name="portfolio"
                      value={form.portfolio}
                      onChange={handleChange}
                      disabled={portfolioFromSellModal}
                    >
                      <option value="">Select Portfolio</option>
                      {portfolioOptions.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {portfolioFromSellModal && (
                      <small className="form-text text-muted">
                        Portfolio set from sell modal - cannot be changed
                      </small>
                    )}
                  </div>
                  {/* Strategy Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="strategy">Strategy</label>
                    <select
                      className="form-control"
                      name="strategy"
                      value={form.strategy}
                      onChange={handleChange}
                    >
                      <option value="">Select Strategy</option>
                      {strategies.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </div>
                  {/* Custodian Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="custodian">Custodian</label>
                    <select
                      className="form-control"
                      name="custodian"
                      value={form.custodian}
                      onChange={handleChange}
                    >
                      <option value="">Select Custodian</option>
                      {custodianOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="settlementMode">Settlement Mode</label>
                    <select
                      id="settlementMode"
                      name="settlementMode"
                      className="form-control"
                      value={form.settlementMode}
                      onChange={handleChange}
                    >
                      <option value="">Select Settlement Mode</option>
                      {settlementModes.map(m => (
                        <option key={`${m.payment_method}-${m.bank_payment_code}`} value={m.bank_payment_code}>
                          {m.payment_method} ({m.bank_payment_code})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Settlement Bank Details (read-only) */}
                  {form.settlementMode && (
                    <div className="form-group" style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 6, padding: 10 }}>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <label className="form-label">Settlement Bank</label>
                          <input className="form-control" value={selectedBankDetails.bank_name || ''} readOnly />
                        </div>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <label className="form-label">Bank Branch</label>
                          <input className="form-control" value={selectedBankDetails.bank_branch || ''} readOnly />
                        </div>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <label className="form-label">Bank Account Number</label>
                          <input className="form-control" value={selectedBankDetails.bank_account_number || ''} readOnly />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Brokerage */}
                  <div className="form-group">
                    <label className="form-label">Brokerage</label>
                    <input
                      name="brokerage"
                      value={form.brokerage}
                      onChange={handleChange}
                      className="form-control"
                      type="number"
                      step="0.000001"
                      readOnly={Boolean(form.broker)}
                    />
                  </div>
                  {/* Next Coupon Date */}
                  <div className="form-group">
                    <label className="form-label">Next Coupon Date</label>
                    <input type="date" name="nextCouponDate" value={form.nextCouponDate} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Last Coupon Date */}
                  <div className="form-group">
                    <label className="form-label">Last Coupon Date</label>
                    <input type="date" name="lastCouponDate" value={form.lastCouponDate} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Number of Days - Interest Accrued */}
                  <div className="form-group">
                    <label className="form-label">Number of Days - Interest Accrued</label>
                    <input name="numberOfDaysInterestAccrued" value={form.numberOfDaysInterestAccrued} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Number of Days for the Coupon Period */}
                  <div className="form-group">
                    <label className="form-label">Number of Days for the Coupon Period</label>
                    <input name="numberOfDaysForCouponPeriod" value={form.numberOfDaysForCouponPeriod} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Accrued Interest */}
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center' }}>
                      Accrued Interest
                      <span
                        style={{ marginLeft: 6, color: '#007bff', cursor: 'pointer' }}
                        title="Accrued Interest is rounded to 4 decimal places as per bond pricing policy."
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <circle cx="8" cy="8" r="8" fill="#007bff" opacity="0.15" />
                          <text x="8" y="12" textAnchor="middle" fontSize="11" fill="#007bff">?</text>
                        </svg>
                      </span>
                    </label>
                    <input
                      name="accruedInterest"
                      value={form.accruedInterest ? parseFloat(form.accruedInterest).toFixed(4) : ''}
                      readOnly
                      style={
                        form.transactionType
                          ? getDerivedFieldStyle(form.transactionType)
                          : { backgroundColor: '#ffeeba', fontWeight: 'bold', color: '#7c4700', border: '2px solid #ffd966' }
                      }
                      className="form-control"
                    />
                    <small style={{ color: '#b8860b' }}>
                      Value is truncated to 4 decimal places, not rounded.
                    </small>
                  </div>
                  {/* Accrued Interest Adjustment */}
                  <div className="form-group">
                    <label className="form-label">Accrued Interest Adjustment</label>
                    <input name="accruedInterestAdjustment" value={form.accruedInterestAdjustment} onChange={handleChange} className="form-control" inputMode="numeric" pattern="[0-9]*" />
                  </div>
                  {/* Coupon Interest */}
                  <div className="form-group">
                    <label className="form-label">Coupon Interest</label>
                    <input name="couponInterest" value={form.couponInterest} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Clean Price */}
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label className="form-label" style={{ display: 'flex', alignItems: 'center' }}>
                      Clean Price
                      <span
                        style={{ marginLeft: 6, color: '#007bff', cursor: 'pointer' }}
                        title="Clean Price is rounded to 4 decimal places as per bond pricing policy."
                      >
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <circle cx="8" cy="8" r="8" fill="#007bff" opacity="0.15" />
                          <text x="8" y="12" textAnchor="middle" fontSize="11" fill="#007bff">?</text>
                        </svg>
                      </span>
                    </label>
                    <input
                      name="cleanPrice"
                      value={form.cleanPrice ? parseFloat(form.cleanPrice).toFixed(4) : ''}
                      readOnly
                      style={
                        form.transactionType
                          ? getDerivedFieldStyle(form.transactionType)
                          : { backgroundColor: '#e3f0ff', fontWeight: 'bold', color: '#007bff', border: '2px solid #b8daff' }
                      }
                      className="form-control"
                    />
                    <small style={{ color: '#007bff' }}>
                      Value is truncated to 4 decimal places, not rounded.
                    </small>
                  </div>
                  {/* Clean Price Adjustment */}
                  <div className="form-group">
                    <label className="form-label">Clean Price Adjustment</label>
                    <input 
                      name="cleanPriceAdjustment" 
                      value={form.cleanPriceAdjustment} 
                      onChange={handleChange} 
                      className="form-control" 
                      type="number"
                      step="0.0001"
                      placeholder="Enter +value to add or -value to subtract"
                    />
                    <small className="form-text text-muted">
                      Enter positive value to increase clean price, negative value to decrease
                    </small>
                  </div>
                  {/* Dirty Price */}
                  <div className="form-group" style={{ position: 'relative', background: '#007bff0a', borderRadius: 8, padding: 8, border: '2px solid #007bff', marginBottom: 12 }}>
                    <label className="form-label" style={{ color: '#fff', background: '#007bff', padding: '3px 8px', borderRadius: 4, display: 'flex', alignItems: 'center' }}>
                      Dirty Price
                      <span
                        style={{ marginLeft: 6, color: '#fff', cursor: 'pointer' }}
                        title="Dirty Price = Clean Price (rounded) + Accrued Interest (rounded). Both values rounded to 4 decimal places."
                      >
                        <svg width="16" height="16" fill="#fff" viewBox="0 0 16 16">
                          <circle cx="8" cy="8" r="8" fill="#fff" opacity="0.18" />
                          <text x="8" y="12" textAnchor="middle" fontSize="11" fill="#007bff">?</text>
                        </svg>
                      </span>
                    </label>
                    <input
                      name="dirtyPrice"
                      value={form.dirtyPrice ? parseFloat(form.dirtyPrice).toFixed(4) : ''}
                      readOnly
                      className="form-control"
                      style={
                        form.transactionType
                          ? getDerivedFieldStyle(form.transactionType)
                          : { backgroundColor: '#e3f0ff', fontWeight: 'bold', color: '#007bff', border: '2px solid #007bff' }
                      }
                    />
                    <small style={{ color: '#007bff', fontWeight: 500 }}>
                      Dirty Price = Clean Price (rounded) + Accrued Interest (rounded to 4 decimals).
                    </small>
                  </div>
                  {/* Currency Dropdown */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="currency">Currency</label>
                    <select
                      id="currency"
                      name="currency"
                      className="form-control"
                      value={form.currency}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Currency</option>
                      {currencyOptions.map(currency => (
                        <option key={currency} value={currency}>{currency}</option>
                      ))}
                    </select>
                  </div>
                  {/* Accrued Interest Calculation */}
                  <div className="form-group">
                    <label className="form-label">Accrued Interest Calculation</label>
                    <input name="accruedInterestCalculation" value={form.accruedInterestCalculation} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Accrued Interest Six Decimals */}
                  <div className="form-group">
                    <label className="form-label">Accrued Interest Six Decimals</label>
                    <input name="accruedInterestSixDecimals" value={form.accruedInterestSixDecimals} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Settlement Amount */}
                  <div className="form-group">
                    <label className="form-label">Settlement Amount</label>
                    <input
                      name="settlementAmount"
                      value={
                        form.settlementAmount && !isNaN(Number(form.settlementAmount))
                          ? Number(form.settlementAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : ''
                      }
                      readOnly
                      style={getDerivedFieldStyle(form.transactionType)}
                      className="form-control"
                    />
                  </div>
                  {/* Issue Date */}
                  <div className="form-group">
                    <label className="form-label">Issue Date</label>
                    <input type="date" name="issueDate" value={form.issueDate} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Maturity Date */}
                  <div className="form-group">
                    <label className="form-label">Maturity Date</label>
                    <input type="date" name="maturityDate" value={form.maturityDate} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Coupon Rate */}
                  <div className="form-group">
                    <label className="form-label">Coupon Rate</label>
                    <input name="couponRate" value={form.couponRate || ''} readOnly style={getDerivedFieldStyle(form.transactionType)} className="form-control" />
                  </div>
                  {/* Save Button */}
                  <div className="form-group" style={{ textAlign: 'right', marginTop: '1.5rem' }}>
                    <button type="submit" className="btn btn-primary" style={{ minWidth: 220 }} disabled={isLoading}>
                      {isLoading ? 'Saving...' : 'Save & Send for Verification'}
                    </button>
                  </div>
                </div>

                <div className="gsec-transactions-list">
                  <RecentDealsDateFilter
                    title="Recent GSec Transactions"
                    filterDateYmd={recentDealsFilterDate}
                    onFilterDateChange={setRecentDealsFilterDate}
                    onRefresh={fetchRecentGsecTransactions}
                    refreshing={isLoading || recentDealsFilterLoading}
                  />
                  <div className="recent-transactions-container" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Deal #</th>
                          <th>Type</th>
                          <th>ISIN</th>
                          <th>Counterparty</th>
                          <th>Face Value</th>
                          <th>Dirty Price <span style={{ color: '#007bff', fontSize: 12 }} title="Truncated to 4 decimals">?</span></th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          if (!Array.isArray(recentGsecTransactions)) {
                            return (
                              <tr>
                                <td colSpan="9" className="text-center">Loading GSec transactions...</td>
                              </tr>
                            );
                          }
                          if (filteredRecentGsecTransactions.length === 0) {
                            return (
                              <tr>
                                <td colSpan="9" className="text-center">
                                  {recentDealsFilterDate
                                    ? `No GSec transactions found for ${recentDealsFilterDate}.`
                                    : 'No GSec transactions found.'}
                                </td>
                              </tr>
                            );
                          }
                          // Group and increment deal numbers per date
                          let lastDate = null;
                          let dealCounter = 0;
                          return filteredRecentGsecTransactions.map((tx, idx) => {
                            // Any logged-in user may edit and resubmit a rejected deal, not just its original creator.
                            const canEditRejected = tx.status === 'rejected';
                            // Use counterparty_name from backend JOIN, or fallback to lookup
                            let cpDisplay = 'N/A';
                            if (tx.counterparty_name) {
                              // Use the counterparty_name from the backend query JOIN
                              cpDisplay = tx.counterparty_name;
                            } else if (tx.counterparty_id || tx.counterparty) {
                              // Fallback: try to find in counterparties array
                              const cpId = tx.counterparty_id || tx.counterparty;
                              const cp = Array.isArray(counterparties) ? counterparties.find(c => {
                                // Match by unique_id (e.g., 'i1') or by id if counterparty_id is integer
                                return String(c.unique_id) === String(cpId) || 
                                       (typeof cpId === 'number' && c.id === cpId);
                              }) : null;
                              if (cp) cpDisplay = cp.short_name + (cp.type ? ` (${cp.type})` : '');
                            }

                            return (
                              <tr key={tx.id} className={tx.status === 'rejected' ? 'rejected-row' : ''}>
                                <td>{tx.trade_date}</td>
                                <td>{tx.deal_number}</td>
                                <td>{tx.transaction_type}</td>
                                <td>{tx.isin}</td>
                                <td>{cpDisplay}</td>
                                <td>{Number(tx.face_value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td>{Number(tx.dirty_price).toFixed(4)}</td>
                                <td>
                                  <span className={`status-badge ${(tx.status || 'pending').toLowerCase()}`}>{tx.status || 'pending'}</span>
                                  {tx.status === 'rejected' && tx.comment && (
                                    <div className="rejection-reason"><small><i>Reason: {tx.comment}</i></small></div>
                                  )}
                                </td>
                                <td>
                                  <button className="btn btn-sm btn-info" onClick={() => handleViewDetails(tx)}>View</button>
                                  <button 
                                    className="btn btn-sm btn-secondary" 
                                    onClick={() => setSelectedTransactionForDocuments(tx)}
                                    style={{ marginLeft: '5px' }}
                                    title="View/Upload Documents"
                                  >
                                    📎 Docs
                                  </button>
                                  <GsecLetterPrintButton
                                    transaction={tx}
                                    style={{ marginLeft: '5px' }}
                                  />
                                  {String(tx.status).toLowerCase() === 'final_approved' && (
                                    <DealConfirmationButton
                                      dealType="gsec"
                                      dealId={tx.id}
                                      refNumberHint={tx.deal_number}
                                      style={{ marginLeft: '5px' }}
                                    />
                                  )}
                                  {canAuthorize(tx) && (
                                    <>
                                      <button className="btn btn-sm btn-success" onClick={(e) => { e.stopPropagation(); setSelectedTransaction(tx); handleApprove(); }}>Approve</button>
                                      <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); setSelectedTransaction(tx); setShowDetails(true); }}>Reject</button>
                                    </>
                                  )}
                                  <button
                                    className="btn btn-sm btn-warning"
                                    onClick={() => handleEditRejectedDeal(tx)}
                                    disabled={!canEditRejected}
                                    style={{ 
                                      marginLeft: '5px',
                                      opacity: canEditRejected ? 1 : 0.5,
                                      cursor: canEditRejected ? 'pointer' : 'not-allowed'
                                    }}
                                  >
                                    Edit
                                  </button>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </form>
            )}
            
            {/* Document Upload Section - Show after transaction is created or when viewing existing transaction */}
            {(showGsecDocumentSection && createdGsecTransactionId) || selectedTransactionForDocuments ? (
              <div style={{ marginTop: '24px', width: '100%', padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h4 style={{ margin: 0 }}>
                    Documents
                    {selectedTransactionForDocuments && (
                      <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                        - Deal #: {selectedTransactionForDocuments.deal_number || selectedTransactionForDocuments.dealNumber}
                      </span>
                    )}
                  </h4>
                  {selectedTransactionForDocuments && (
                    <button 
                      className="btn btn-sm btn-secondary" 
                      onClick={() => setSelectedTransactionForDocuments(null)}
                    >
                      Close
                    </button>
                  )}
                </div>
                <DocumentUpload
                  transactionType="gsec"
                  transactionId={selectedTransactionForDocuments ? (selectedTransactionForDocuments.id || selectedTransactionForDocuments.deal_number) : createdGsecTransactionId}
                  onUploadSuccess={(documents) => {
                    setSuccess('Document(s) uploaded successfully');
                    if (!selectedTransactionForDocuments) {
                      setShowGsecDocumentSection(false);
                    }
                  }}
                  onUploadError={(error) => {
                    setError('Failed to upload document: ' + (error.response?.data?.error || error.message));
                  }}
                  multiple={true}
                />
                <DocumentList
                  transactionType="gsec"
                  transactionId={selectedTransactionForDocuments ? (selectedTransactionForDocuments.id || selectedTransactionForDocuments.deal_number) : createdGsecTransactionId}
                  onDocumentDeleted={() => {}}
                />
              </div>
            ) : null}

            {/* Rejection form (only shown for pending transactions to authorizers) */}
            {isAuthorizer && selectedTransaction && selectedTransaction.status === 'pending' && (
              <div className="reject-form mt-4">
                <h6>Authorization Actions</h6>
                <div className="form-group">
                  <label htmlFor="comment">Comment (required for rejection)</label>
                  <textarea
                    id="comment"
                    className="form-control"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Provide a reason if rejecting this transaction"
                    rows="3"
                  ></textarea>
                </div>
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-success mr-2"
                    onClick={handleApprove}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Processing...' : 'Approve'}
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleReject}
                    disabled={isLoading || !comment.trim()}
                  >
                    {isLoading ? 'Processing...' : 'Reject'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default FixedIncomeGsecPage;
